import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc, getDoc, updateDoc, deleteDoc, writeBatch,
  collection, getDocs, addDoc, deleteDoc as deleteSubDoc,
  query, orderBy, Timestamp,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import useAdminAuth from '../../../hooks/useAdminAuth';
import {
  HiOutlineArrowLeft, HiOutlineTrash, HiPlus,
  HiChevronDown, HiChevronRight, HiChevronLeft,
  HiOutlineInformationCircle, HiOutlineClock, HiOutlineUserGroup,
  HiOutlineDocumentText, HiOutlineVideoCamera, HiOutlineClipboardDocumentCheck,
  HiOutlineClipboardDocumentList, HiOutlineAcademicCap, HiOutlineMegaphone,
  HiOutlinePencilSquare,
} from 'react-icons/hi2';
import { IoSearchOutline, IoCloseCircle } from 'react-icons/io5';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { friendlyDate, friendlyTimestamp, toLocalDateStr } from '../../../utils/dateFormat';
const CLASS_COLORS = ['#4A7CFE', '#22C55E', '#F59E0B', '#EF4444', '#A855F7', '#06B6D4', '#F97316', '#EC4899'];
const DAYS = ['월', '화', '수', '목', '금', '토', '일'];
const ATTENDANCE_STATUS = { present: '출석', late: '지각', absent: '결석', excused: '사유결석' };

// ─── 접기/펼치기 섹션 컴포넌트 ───
const CollapsibleSection = ({ title, badge, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Section>
      <SectionHeader onClick={() => setOpen(!open)}>
        <SectionHeaderLeft>
          {open ? <HiChevronDown size={18} /> : <HiChevronRight size={18} />}
          <SectionTitle>{title}</SectionTitle>
          {badge !== undefined && <Badge>{badge}</Badge>}
        </SectionHeaderLeft>
      </SectionHeader>
      {open && <SectionBody>{children}</SectionBody>}
    </Section>
  );
};

// ═══════════════════════════════════════
// 메인 컴포넌트
// ═══════════════════════════════════════
const AdminClassDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit, canDelete, isTeacher, admin } = useAdminAuth();

  const [cls, setCls] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teachers, setTeachers] = useState([]);
  const [allStudents, setAllStudents] = useState([]);

  // 기본 정보
  const [form, setForm] = useState({ name: '', subject: '', teacherId: '', room: '', color: '#4A7CFE', attendanceMode: 'self' });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState('');

  // 스케줄 (정규)
  const [schedules, setSchedules] = useState([]);
  const [newDay, setNewDay] = useState('월');
  const [newTime, setNewTime] = useState('09:00');
  const [newEndTime, setNewEndTime] = useState('10:00');

  // 보강 수업
  const [supplements, setSupplements] = useState([]);
  const [supTime, setSupTime] = useState('09:00');
  const [supEndTime, setSupEndTime] = useState('10:00');
  const [supDate, setSupDate] = useState(toLocalDateStr(new Date()));

  // 학생
  const [studentIds, setStudentIds] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');

  // 수업 자료
  const [materials, setMaterials] = useState([]);
  const [allLibraryMaterials, setAllLibraryMaterials] = useState([]);
  const [showMaterialPicker, setShowMaterialPicker] = useState(false);
  const [materialSearch, setMaterialSearch] = useState('');

  // 수업 동영상
  const [videos, setVideos] = useState([]);
  const [showVideoPicker, setShowVideoPicker] = useState(false);
  const [videoSearch, setVideoSearch] = useState('');

  // 출석
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [attendanceDate, setAttendanceDate] = useState(null); // 선택된 날짜
  const [attendanceType, setAttendanceType] = useState(''); // 'regular' | 'supplement'
  const [attendanceMap, setAttendanceMap] = useState({});
  const [attNoteOpenSid, setAttNoteOpenSid] = useState(null);
  const [calMonth, setCalMonth] = useState(new Date()); // 달력 현재 월

  // 과제
  const [assignments, setAssignments] = useState([]);
  const [assignForm, setAssignForm] = useState({ title: '', description: '', dueDate: '' });
  const [assignCalMonth, setAssignCalMonth] = useState(new Date());
  const [assignSelDate, setAssignSelDate] = useState(null);

  // 성적
  const [grades, setGrades] = useState([]);
  const [gradeForm, setGradeForm] = useState({ examName: '', date: '', maxScore: 100 });
  const [gradeCalMonth, setGradeCalMonth] = useState(new Date());
  const [gradeSelDate, setGradeSelDate] = useState(null);
  const [scoreModal, setScoreModal] = useState(null); // grade object for score editing
  const [scoreInputs, setScoreInputs] = useState({});

  // 공지사항
  const [notices, setNotices] = useState([]);
  const [noticeForm, setNoticeForm] = useState({ title: '', content: '' });

  // 수업 일지
  const [logs, setLogs] = useState([]);
  const [logForm, setLogForm] = useState({ date: toLocalDateStr(new Date()), content: '' });
  const [logCalMonth, setLogCalMonth] = useState(new Date());
  const [logSelDate, setLogSelDate] = useState(null);

  // 메모
  const [memos, setMemos] = useState([]);
  const [memoForm, setMemoForm] = useState({ title: '', content: '' });

  // 모달
  const [activeModal, setActiveModal] = useState(null);

  // ─── 데이터 로드 ───
  const subRef = useCallback((sub) => collection(db, 'classes', id, sub), [id]);

  const load = async () => {
    setLoading(true);
    const [classDoc, userSnap, libSnap] = await Promise.all([
      getDoc(doc(db, 'classes', id)),
      getDocs(collection(db, 'users')),
      getDocs(query(collection(db, 'materials'), orderBy('createdAt', 'desc'))).catch(() => ({ docs: [] })),
    ]);
    if (!classDoc.exists()) { navigate('/admin/classes'); return; }

    const data = { id: classDoc.id, ...classDoc.data() };
    const users = userSnap.docs.map((d) => ({ uid: d.id, ...d.data() }));

    setCls(data);
    setForm({
      name: data.name || '', subject: data.subject || '', teacherId: data.teacherId || '',
      room: data.room || '', color: data.color || '#4A7CFE',
      attendanceMode: data.attendanceMode || 'self',
    });
    setSchedules(data.schedule || []);
    setSupplements(data.supplements || []);
    setStudentIds(data.studentIds || []);
    setTeachers(users.filter((u) => u.role === 'teacher'));
    setAllStudents(users.filter((u) => u.role === 'student'));
    setAllLibraryMaterials(libSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

    // 서브컬렉션 로드
    const [matSnap, vidSnap, attSnap, asgSnap, grdSnap, ntcSnap, logSnap, memoSnap] = await Promise.all([
      getDocs(query(subRef('materials'), orderBy('createdAt', 'desc'))).catch(() => ({ docs: [] })),
      getDocs(query(subRef('videos'), orderBy('createdAt', 'desc'))).catch(() => ({ docs: [] })),
      getDocs(query(subRef('attendance'), orderBy('date', 'desc'))).catch(() => ({ docs: [] })),
      getDocs(query(subRef('assignments'), orderBy('createdAt', 'desc'))).catch(() => ({ docs: [] })),
      getDocs(query(subRef('grades'), orderBy('createdAt', 'desc'))).catch(() => ({ docs: [] })),
      getDocs(query(subRef('notices'), orderBy('createdAt', 'desc'))).catch(() => ({ docs: [] })),
      getDocs(query(subRef('logs'), orderBy('date', 'desc'))).catch(() => ({ docs: [] })),
      getDocs(query(subRef('memos'), orderBy('createdAt', 'desc'))).catch(() => ({ docs: [] })),
    ]);

    setMaterials(matSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setVideos(vidSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setAttendanceRecords(attSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setAssignments(asgSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setGrades(grdSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setNotices(ntcSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setLogs(logSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setMemos(memoSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  useEffect(() => { load(); }, [id]);

  // ─── 기본 정보 ───
  const saveBasicInfo = async () => {
    setSaving(true);
    const teacher = teachers.find((t) => t.uid === form.teacherId);
    await updateDoc(doc(db, 'classes', id), {
      name: form.name, subject: form.subject,
      teacherId: form.teacherId, teacherName: teacher?.name || '',
      room: form.room, color: form.color,
      attendanceMode: form.attendanceMode || 'self',
    });
    setSaving(false);
  };

  // ─── 정규 수업 스케줄 ───
  const addSchedule = async () => {
    if (!newDay || !newTime) return;
    const entry = { day: newDay, time: newTime };
    if (newEndTime) entry.endTime = newEndTime;
    const updated = [...schedules, entry];
    setSchedules(updated);
    await updateDoc(doc(db, 'classes', id), { schedule: updated });
    setNewTime('09:00');
    setNewEndTime('10:00');
  };
  const removeSchedule = async (idx) => {
    const updated = schedules.filter((_, i) => i !== idx);
    setSchedules(updated);
    await updateDoc(doc(db, 'classes', id), { schedule: updated });
  };

  // ─── 보강 수업 스케줄 ───
  const addSupplement = async () => {
    if (!supDate || !supTime) return;
    const entry = { date: supDate, time: supTime };
    if (supEndTime) entry.endTime = supEndTime;
    const updated = [...supplements, entry];
    setSupplements(updated);
    await updateDoc(doc(db, 'classes', id), { supplements: updated });
    setSupTime('09:00');
    setSupEndTime('10:00');
    setSupDate('');
  };
  const removeSupplement = async (idx) => {
    const updated = supplements.filter((_, i) => i !== idx);
    setSupplements(updated);
    await updateDoc(doc(db, 'classes', id), { supplements: updated });
  };

  // ─── 학생 ───
  const addStudent = async (uid) => {
    if (studentIds.includes(uid)) return;
    const updated = [...studentIds, uid];
    setStudentIds(updated);
    await updateDoc(doc(db, 'classes', id), { studentIds: updated, students: updated.length });
  };
  const removeStudent = async (uid) => {
    const st = allStudents.find(s => s.uid === uid);
    const studentName = st?.name || uid;
    if (!window.confirm(`'${studentName}' 학생을 이 클래스에서 제거하시겠습니까?\n\n해당 클래스의 학생 관련 자료가 삭제됩니다.`)) return;
    const updated = studentIds.filter((s) => s !== uid);
    setStudentIds(updated);
    await updateDoc(doc(db, 'classes', id), { studentIds: updated, students: updated.length });
  };
  const filteredStudents = allStudents.filter((s) => {
    if (studentIds.includes(s.uid)) return false;
    if (!studentSearch) return true;
    return s.name?.includes(studentSearch);
  });

  // ─── 수업 자료 (자료실에서 파일만 가져오기 + 자료명/설명 직접 입력) ───
  const [matForm, setMatForm] = useState({ title: '', description: '', libraryItem: null });

  const selectLibraryFile = (libMat) => {
    setMatForm({ ...matForm, libraryItem: libMat });
    setShowMaterialPicker(false);
    setMaterialSearch('');
  };
  const addMaterial = async () => {
    if (!matForm.title || !matForm.libraryItem) return;
    const lm = matForm.libraryItem;
    const data = {
      libraryId: lm.id,
      title: matForm.title,
      description: matForm.description || '',
      fileName: lm.fileName || '',
      fileUrl: lm.fileUrl || '',
      fileType: lm.fileType || 'file',
      createdAt: Timestamp.now(),
    };
    const docRef = await addDoc(subRef('materials'), data);
    setMaterials([{ id: docRef.id, ...data }, ...materials]);
    setMatForm({ title: '', description: '', libraryItem: null });
  };
  const removeMaterial = async (mat) => {
    if (!window.confirm(`'${mat.title}' 자료를 제거하시겠습니까?`)) return;
    await deleteSubDoc(doc(db, 'classes', id, 'materials', mat.id));
    setMaterials(materials.filter((m) => m.id !== mat.id));
  };
  const filteredLibraryMaterials = allLibraryMaterials.filter((lm) => {
    if (!materialSearch) return true;
    return lm.title?.toLowerCase().includes(materialSearch.toLowerCase())
      || lm.tags?.some((t) => t.toLowerCase().includes(materialSearch.toLowerCase()));
  });

  // ─── 수업 동영상 (자료실에서 파일 가져오기 + 제목/설명 직접 입력) ───
  const [vidForm, setVidForm] = useState({ title: '', description: '', libraryItem: null });

  const selectLibraryVideo = (libMat) => {
    setVidForm({ ...vidForm, libraryItem: libMat });
    setShowVideoPicker(false);
    setVideoSearch('');
  };
  const addVideo = async () => {
    if (!vidForm.title || !vidForm.libraryItem) return;
    const lm = vidForm.libraryItem;
    const data = {
      libraryId: lm.id,
      title: vidForm.title,
      description: vidForm.description || '',
      fileName: lm.fileName || '',
      fileUrl: lm.fileUrl || '',
      fileType: lm.fileType || 'video',
      createdAt: Timestamp.now(),
    };
    const docRef = await addDoc(subRef('videos'), data);
    setVideos([{ id: docRef.id, ...data }, ...videos]);
    setVidForm({ title: '', description: '', libraryItem: null });
  };
  const removeVideo = async (vid) => {
    if (!window.confirm(`'${vid.title}' 영상을 삭제하시겠습니까?`)) return;
    await deleteSubDoc(doc(db, 'classes', id, 'videos', vid.id));
    setVideos(videos.filter((v) => v.id !== vid.id));
  };
  const filteredLibraryVideos = allLibraryMaterials.filter((lm) => {
    if (lm.fileType !== 'video') return false;
    if (!videoSearch) return true;
    return lm.title?.toLowerCase().includes(videoSearch.toLowerCase())
      || lm.tags?.some((t) => t.toLowerCase().includes(videoSearch.toLowerCase()));
  });

  // ─── 출석 ───
  const attKey = attendanceDate ? `${attendanceDate}_${attendanceType}` : null;

  const initAttendanceForDate = () => {
    if (!attendanceDate) return;
    const map = {};
    studentIds.forEach((sid) => { map[sid] = { status: 'present', note: '' }; });
    const existing = attendanceRecords.find((r) => r.date === attendanceDate && r.type === attendanceType);
    if (existing?.records) {
      Object.entries(existing.records).forEach(([sid, val]) => {
        // 기존 데이터 호환 (string이면 변환)
        if (typeof val === 'string') {
          map[sid] = { status: val, note: '' };
        } else {
          map[sid] = val;
        }
      });
    }
    setAttendanceMap(map);
  };
  useEffect(() => { if (studentIds.length > 0 && attendanceDate) initAttendanceForDate(); }, [attendanceDate, attendanceType, studentIds]);

  const saveAttendance = async () => {
    if (!attendanceDate) return;
    const existing = attendanceRecords.find((r) => r.date === attendanceDate && r.type === attendanceType);
    if (existing) {
      await updateDoc(doc(db, 'classes', id, 'attendance', existing.id), { records: attendanceMap });
      setAttendanceRecords(attendanceRecords.map((r) => r.id === existing.id ? { ...r, records: attendanceMap } : r));
    } else {
      const data = { date: attendanceDate, type: attendanceType, records: attendanceMap, createdAt: Timestamp.now() };
      const docRef = await addDoc(subRef('attendance'), data);
      setAttendanceRecords([{ id: docRef.id, ...data }, ...attendanceRecords]);
    }
    alert('출석이 저장되었습니다.');
  };

  const selectScheduleForAttendance = (dateStr, type) => {
    setAttendanceDate(dateStr);
    setAttendanceType(type);
  };

  // ─── 셀프 출석 세션 열기/닫기 ───
  const toggleSelfCheck = async (dateStr, type) => {
    const existing = attendanceRecords.find((r) => r.date === dateStr && r.type === type);
    if (existing) {
      const newOpen = !existing.selfCheckOpen;
      const updates = { selfCheckOpen: newOpen };
      if (newOpen) updates.selfCheckDeadline = Timestamp.fromDate(new Date(Date.now() + 10 * 60 * 1000));
      await updateDoc(doc(db, 'classes', id, 'attendance', existing.id), updates);
      setAttendanceRecords(attendanceRecords.map((r) => r.id === existing.id ? { ...r, ...updates } : r));
    } else {
      const data = {
        date: dateStr, type, mode: 'self', records: {},
        selfCheckOpen: true,
        selfCheckDeadline: Timestamp.fromDate(new Date(Date.now() + 10 * 60 * 1000)),
        createdAt: Timestamp.now(),
      };
      const docRef = await addDoc(subRef('attendance'), data);
      setAttendanceRecords([{ id: docRef.id, ...data }, ...attendanceRecords]);
    }
  };

  const isSelfMode = form.attendanceMode === 'self';

  // ─── 달력 헬퍼 ───
  const getCalendarDays = () => {
    const y = calMonth.getFullYear();
    const m = calMonth.getMonth();
    const firstDay = new Date(y, m, 1).getDay(); // 0=일
    const lastDate = new Date(y, m + 1, 0).getDate();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= lastDate; d++) days.push(d);
    return days;
  };

  const getDayOfWeek = (y, m, d) => {
    const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
    return dayNames[new Date(y, m, d).getDay()];
  };

  // 개설일 문자열
  const openDateStr = (() => {
    if (!cls?.createdAt) return null;
    const d = cls.createdAt?.toDate ? cls.createdAt.toDate() : new Date(cls.createdAt);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  const getSchedulesForDay = (dayNum) => {
    if (!dayNum) return { regular: null, supplement: null, dateStr: '', beforeOpen: false };
    const y = calMonth.getFullYear();
    const m = calMonth.getMonth();
    const dow = getDayOfWeek(y, m, dayNum);
    const dateStr = `${y}-${String(m + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;

    // 개설일 이전이면 빈 결과
    if (openDateStr && dateStr < openDateStr) {
      return { regular: null, supplement: null, dateStr, beforeOpen: true };
    }

    const regularItems = schedules.filter((s) => s.day === dow);
    const supplementItem = supplements.find((s) => s.date === dateStr);
    return {
      regular: regularItems.length > 0 ? regularItems : null,
      supplement: supplementItem || null,
      dateStr,
      beforeOpen: false,
    };
  };

  const hasAttendanceRecord = (dateStr, type) => {
    return attendanceRecords.some((r) => r.date === dateStr && r.type === type);
  };

  const getAttendanceSummary = (dateStr, type) => {
    const record = attendanceRecords.find((r) => r.date === dateStr && r.type === type);
    if (!record?.records) return null;
    const counts = { present: 0, late: 0, absent: 0, excused: 0 };
    Object.values(record.records).forEach((val) => {
      const status = typeof val === 'string' ? val : val.status;
      if (counts[status] !== undefined) counts[status]++;
    });
    return counts;
  };

  // ─── 과제 ───
  const addAssignment = async () => {
    if (!assignForm.title || !assignForm.dueDate) return;
    const docRef = await addDoc(subRef('assignments'), {
      ...assignForm, createdAt: Timestamp.now(),
    });
    setAssignments([{ id: docRef.id, ...assignForm, createdAt: Timestamp.now() }, ...assignments]);
    setAssignForm({ title: '', description: '', dueDate: assignForm.dueDate });
  };
  const removeAssignment = async (asg) => {
    if (!window.confirm(`'${asg.title}' 과제를 삭제하시겠습니까?`)) return;
    await deleteSubDoc(doc(db, 'classes', id, 'assignments', asg.id));
    setAssignments(assignments.filter((a) => a.id !== asg.id));
  };
  const getAssignmentsForDate = (dateStr) => assignments.filter((a) => a.dueDate === dateStr);

  const getSubmissionSummary = (dateStr) => {
    const dayAssigns = getAssignmentsForDate(dateStr);
    if (dayAssigns.length === 0 || studentIds.length === 0) return null;
    let submitted = 0;
    let notSubmitted = 0;
    dayAssigns.forEach((a) => {
      studentIds.forEach((sid) => {
        if (a.submissions?.[sid]?.status === 'submitted') submitted++;
        else notSubmitted++;
      });
    });
    return { submitted, notSubmitted };
  };

  const getCalDays = (month) => {
    const y = month.getFullYear();
    const m = month.getMonth();
    const firstDay = new Date(y, m, 1).getDay();
    const lastDate = new Date(y, m + 1, 0).getDate();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= lastDate; d++) days.push(d);
    return days;
  };
  const toDateStr = (month, dayNum) => {
    const y = month.getFullYear();
    const m = month.getMonth();
    return `${y}-${String(m + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
  };

  // ─── 성적 ───
  const addGrade = async () => {
    if (!gradeForm.examName || !gradeForm.date) return;
    const docRef = await addDoc(subRef('grades'), {
      ...gradeForm, scores: {}, createdAt: Timestamp.now(),
    });
    setGrades([{ id: docRef.id, ...gradeForm, scores: {}, createdAt: Timestamp.now() }, ...grades]);
    setGradeForm({ examName: '', date: gradeForm.date, maxScore: 100 });
  };
  const openScoreModal = (grade) => {
    const inputs = {};
    studentIds.forEach(sid => {
      const s = (grade.scores || {})[sid];
      inputs[sid] = s !== undefined && s !== null ? String(s) : 'absent';
    });
    setScoreInputs(inputs);
    setScoreModal(grade);
  };
  const saveScores = async () => {
    if (!scoreModal) return;
    const scores = {};
    Object.entries(scoreInputs).forEach(([sid, val]) => {
      if (val !== '' && val !== 'absent') scores[sid] = Number(val);
    });
    await updateDoc(doc(db, 'classes', id, 'grades', scoreModal.id), { scores });
    setGrades(grades.map(g => g.id === scoreModal.id ? { ...g, scores } : g));
    setScoreModal(null);
  };
  const removeGrade = async (g) => {
    if (!window.confirm(`'${g.examName}' 시험 기록을 삭제하시겠습니까?`)) return;
    await deleteSubDoc(doc(db, 'classes', id, 'grades', g.id));
    setGrades(grades.filter((gr) => gr.id !== g.id));
  };
  const getGradesForDate = (dateStr) => grades.filter((g) => g.date === dateStr);
  const getLogsForDate = (dateStr) => logs.filter((l) => l.date === dateStr);

  // ─── 공지사항 ───
  const addNotice = async () => {
    if (!noticeForm.title) return;
    const docRef = await addDoc(subRef('notices'), {
      ...noticeForm, createdAt: Timestamp.now(),
    });
    setNotices([{ id: docRef.id, ...noticeForm, createdAt: Timestamp.now() }, ...notices]);
    setNoticeForm({ title: '', content: '' });
  };
  const removeNotice = async (n) => {
    if (!window.confirm(`'${n.title}' 공지를 삭제하시겠습니까?`)) return;
    await deleteSubDoc(doc(db, 'classes', id, 'notices', n.id));
    setNotices(notices.filter((nt) => nt.id !== n.id));
  };

  // ─── 수업 일지 ───
  const addLog = async () => {
    if (!logForm.content) return;
    const docRef = await addDoc(subRef('logs'), {
      ...logForm, createdAt: Timestamp.now(),
    });
    setLogs([{ id: docRef.id, ...logForm, createdAt: Timestamp.now() }, ...logs]);
    setLogForm({ date: toLocalDateStr(new Date()), content: '' });
  };
  const removeLog = async (l) => {
    if (!window.confirm('이 수업 일지를 삭제하시겠습니까?')) return;
    await deleteSubDoc(doc(db, 'classes', id, 'logs', l.id));
    setLogs(logs.filter((lg) => lg.id !== l.id));
  };

  // ─── 메모 ───
  const addMemo = async () => {
    if (!memoForm.content) return;
    const docRef = await addDoc(subRef('memos'), {
      ...memoForm, createdAt: Timestamp.now(),
    });
    setMemos([{ id: docRef.id, ...memoForm, createdAt: Timestamp.now() }, ...memos]);
    setMemoForm({ title: '', content: '' });
  };
  const removeMemo = async (m) => {
    if (!window.confirm('이 메모를 삭제하시겠습니까?')) return;
    await deleteSubDoc(doc(db, 'classes', id, 'memos', m.id));
    setMemos(memos.filter((mm) => mm.id !== m.id));
  };

  // ─── 클래스 삭제 ───
  const handleDeleteClass = async () => {
    const msg = studentIds.length > 0
      ? `'${cls.name}' 클래스를 삭제하시겠습니까?\n\n수강 학생 ${studentIds.length}명의 클래스 정보도 함께 삭제됩니다.`
      : `'${cls.name}' 클래스를 삭제하시겠습니까?`;
    if (!window.confirm(msg)) return;
    if (!window.confirm('정말 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) return;

    setDeleting(true);
    const subCollections = ['attendance', 'assignments', 'grades', 'notices', 'materials', 'videos', 'logs', 'memos'];
    const subLabels = { attendance: '출석 기록', assignments: '과제', grades: '성적', notices: '공지사항', materials: '수업 자료', videos: '수업 동영상', logs: '수업 일지', memos: '메모' };
    try {
      for (const sub of subCollections) {
        setDeleteProgress(`${subLabels[sub]} 삭제 중...`);
        const snap = await getDocs(collection(db, 'classes', id, sub));
        if (snap.docs.length > 0) {
          const batch = writeBatch(db);
          snap.docs.forEach(d => batch.delete(d.ref));
          await batch.commit();
        }
      }
      setDeleteProgress('클래스 삭제 중...');
      await deleteDoc(doc(db, 'classes', id));
      setDeleteProgress('완료!');
      await new Promise(r => setTimeout(r, 500));
      navigate('/admin/classes');
    } catch (err) {
      console.error('클래스 삭제 실패:', err);
      alert('삭제 중 오류가 발생했습니다.');
      setDeleting(false);
      setDeleteProgress('');
    }
  };

  // 헬퍼
  const getStudentName = (sid) => allStudents.find((s) => s.uid === sid)?.name || sid;
  const formatDate = (ts) => {
    if (!ts) return '-';
    if (typeof ts === 'string') return friendlyDate(ts);
    return friendlyTimestamp(ts);
  };

  // 섹션 모달 헬퍼
  const closeModal = () => setActiveModal(null);

  if (loading) return <Container><LoadingSpinner /></Container>;
  if (!cls) return null;

  return (
    <Container>
      {deleting && (
        <DeleteOverlay>
          <DeleteSpinner />
          <DeleteText>{deleteProgress}</DeleteText>
        </DeleteOverlay>
      )}
      {/* ── 상단 헤더 ── */}
      <TopBar>
        <BackBtn onClick={() => navigate('/admin/classes')}>
          <HiOutlineArrowLeft size={18} /> 클래스 목록
        </BackBtn>
        <TopRight>
          <ClassTitle><ColorDot $color={form.color} />{cls.name}</ClassTitle>
          {canDelete && (
            <DeleteClassBtn onClick={handleDeleteClass}>
              <HiOutlineTrash size={16} /> 삭제
            </DeleteClassBtn>
          )}
        </TopRight>
      </TopBar>

      {/* ── 카드 그리드 ── */}
      <CardGrid>
        <BasicInfoCard onClick={() => setActiveModal('basic')}>
          <BasicInfoTop>
            <BasicInfoItem>
              <BasicInfoLabel>담당 강사</BasicInfoLabel>
              <BasicInfoValue>{teachers.find(t => t.uid === form.teacherId)?.name || '미배정'}</BasicInfoValue>
            </BasicInfoItem>
            <BasicInfoItem>
              <BasicInfoLabel>학생 수</BasicInfoLabel>
              <BasicInfoValue>{studentIds.length}명</BasicInfoValue>
            </BasicInfoItem>
            <BasicInfoItem>
              <BasicInfoLabel>강의실</BasicInfoLabel>
              <BasicInfoValue>{form.room || '-'}</BasicInfoValue>
            </BasicInfoItem>
            <BasicInfoItem>
              <BasicInfoLabel>출석 모드</BasicInfoLabel>
              <BasicInfoValue>{form.attendanceMode === 'self' ? '위치 기반 (학생)' : '자동 (강사 체크)'}</BasicInfoValue>
            </BasicInfoItem>
          </BasicInfoTop>
          <BasicInfoSchedule>
            <BasicInfoLabel>정규 수업</BasicInfoLabel>
            <BasicInfoScheduleList>
              {schedules.map((s, i) => (
                <BasicInfoScheduleChip key={i}>매주 {s.day}요일 {s.time}{s.endTime ? ` ~ ${s.endTime}` : ''}</BasicInfoScheduleChip>
              ))}
              {canEdit && <BasicInfoScheduleAddChip onClick={(e) => { e.stopPropagation(); setActiveModal('schedule-regular'); }}><HiPlus size={16} /> 정규 수업 추가</BasicInfoScheduleAddChip>}
            </BasicInfoScheduleList>
            <BasicInfoLabel style={{ marginTop: 12 }}>보강 수업</BasicInfoLabel>
            <BasicInfoScheduleList>
              {supplements.filter(s => s.date >= toLocalDateStr(new Date())).map((s, i) => (
                <BasicInfoSupChip key={i}>{friendlyDate(s.date)} {s.time}{s.endTime ? ` ~ ${s.endTime}` : ''}</BasicInfoSupChip>
              ))}
              {canEdit && <BasicInfoScheduleAddChip onClick={(e) => { e.stopPropagation(); setActiveModal('schedule-supplement'); }}><HiPlus size={16} /> 보강 수업 추가</BasicInfoScheduleAddChip>}
            </BasicInfoScheduleList>
          </BasicInfoSchedule>
        </BasicInfoCard>

        <DashCard onClick={() => setActiveModal('students')}>
          <DashCardIcon $bg="#F5F3FF" $color="#A855F7"><HiOutlineUserGroup size={22} /></DashCardIcon>
          <DashCardInfo>
            <DashCardTitle>수강 학생</DashCardTitle>
            <DashCardSub>{studentIds.length}명 등록</DashCardSub>
            <DashCardDetail>{studentIds.length > 0 ? studentIds.map(sid => getStudentName(sid)).join(', ') : '학생 미등록'}</DashCardDetail>
          </DashCardInfo>
        </DashCard>

        <DashCard onClick={() => setActiveModal('materials')}>
          <DashCardIcon $bg="#FFFBEB" $color="#F59E0B"><HiOutlineDocumentText size={22} /></DashCardIcon>
          <DashCardInfo>
            <DashCardTitle>수업 자료</DashCardTitle>
            <DashCardSub>{materials.length}개 등록</DashCardSub>
            <DashCardDetail>{materials.length > 0 ? materials.slice(0, 2).map(m => m.title).join(', ') + (materials.length > 2 ? ' ...' : '') : '자료 없음'}</DashCardDetail>
          </DashCardInfo>
        </DashCard>

        <DashCard onClick={() => setActiveModal('videos')}>
          <DashCardIcon $bg="#FEF2F2" $color="#EF4444"><HiOutlineVideoCamera size={22} /></DashCardIcon>
          <DashCardInfo>
            <DashCardTitle>수업 동영상</DashCardTitle>
            <DashCardSub>{videos.length}개 등록</DashCardSub>
            <DashCardDetail>{videos.length > 0 ? videos.slice(0, 2).map(v => v.title).join(', ') + (videos.length > 2 ? ' ...' : '') : '동영상 없음'}</DashCardDetail>
          </DashCardInfo>
        </DashCard>

        <DashCard onClick={() => setActiveModal('attendance')}>
          <DashCardIcon $bg="#ECFEFF" $color="#06B6D4"><HiOutlineClipboardDocumentCheck size={22} /></DashCardIcon>
          <DashCardInfo>
            <DashCardTitle>출석 관리</DashCardTitle>
            <DashCardSub>{attendanceRecords.length}건 기록 · 학생 {studentIds.length}명</DashCardSub>
            <DashCardDetail>{attendanceRecords.length > 0 ? `최근: ${attendanceRecords[0]?.date || '-'}` : '출석 기록 없음'}</DashCardDetail>
          </DashCardInfo>
        </DashCard>

        <DashCard onClick={() => setActiveModal('assignments')}>
          <DashCardIcon $bg="#FDF2F8" $color="#EC4899"><HiOutlineClipboardDocumentList size={22} /></DashCardIcon>
          <DashCardInfo>
            <DashCardTitle>과제 관리</DashCardTitle>
            <DashCardSub>{assignments.length}개 등록</DashCardSub>
            <DashCardDetail>{assignments.length > 0 ? `최근: ${assignments[0]?.title || '-'}` : '과제 없음'}</DashCardDetail>
          </DashCardInfo>
        </DashCard>

        <DashCard onClick={() => setActiveModal('grades')}>
          <DashCardIcon $bg="#FFF7ED" $color="#F97316"><HiOutlineAcademicCap size={22} /></DashCardIcon>
          <DashCardInfo>
            <DashCardTitle>성적/시험 관리</DashCardTitle>
            <DashCardSub>{grades.length}건 등록</DashCardSub>
            <DashCardDetail>{grades.length > 0 ? `최근: ${grades[0]?.examName || '-'}` : '시험 기록 없음'}</DashCardDetail>
          </DashCardInfo>
        </DashCard>

        <DashCard onClick={() => setActiveModal('notices')}>
          <DashCardIcon $bg="#F5F3FF" $color="#8B5CF6"><HiOutlineMegaphone size={22} /></DashCardIcon>
          <DashCardInfo>
            <DashCardTitle>공지사항</DashCardTitle>
            <DashCardSub>{notices.length}개 등록</DashCardSub>
            <DashCardDetail>{notices.length > 0 ? notices[0]?.title || '-' : '공지 없음'}</DashCardDetail>
          </DashCardInfo>
        </DashCard>

        <DashCard onClick={() => setActiveModal('logs')}>
          <DashCardIcon $bg="#F0FDFA" $color="#14B8A6"><HiOutlinePencilSquare size={22} /></DashCardIcon>
          <DashCardInfo>
            <DashCardTitle>수업 일지</DashCardTitle>
            <DashCardSub>{logs.length}건 기록</DashCardSub>
            <DashCardDetail>{logs.length > 0 ? `최근: ${logs[0]?.date || '-'}` : '일지 없음'}</DashCardDetail>
          </DashCardInfo>
        </DashCard>

        <DashCard onClick={() => setActiveModal('memos')}>
          <DashCardIcon $bg="#FEF9C3" $color="#CA8A04"><HiOutlineDocumentText size={22} /></DashCardIcon>
          <DashCardInfo>
            <DashCardTitle>메모</DashCardTitle>
            <DashCardSub>{memos.length}개</DashCardSub>
            <DashCardDetail>{memos.length > 0 ? (memos[0]?.title || memos[0]?.content?.substring(0, 20) || '-') : '메모 없음'}</DashCardDetail>
          </DashCardInfo>
        </DashCard>
      </CardGrid>

      {/* ══ 기본 정보 모달 ══ */}
      {activeModal === 'basic' && (
        <SectionModal title="기본 정보" onClose={closeModal}>
          <FormGrid>
            <FormGroup>
              <Label>클래스명 *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} disabled={!canEdit} />
            </FormGroup>
            <FormGroup>
              <Label>과목</Label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} disabled={!canEdit} />
            </FormGroup>
            <FormGroup>
              <Label>담당 강사</Label>
              {isTeacher ? (
                <FixedValue>{admin.name} (본인)</FixedValue>
              ) : (
                <Select value={form.teacherId} onChange={(e) => setForm({ ...form, teacherId: e.target.value })} disabled={!canEdit}>
                  <option value="">강사 선택</option>
                  {teachers.map((t) => <option key={t.uid} value={t.uid}>{t.name}</option>)}
                </Select>
              )}
            </FormGroup>
            <FormGroup>
              <Label>강의실</Label>
              <Input value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} disabled={!canEdit} />
            </FormGroup>
            <FormGroup $full>
              <Label>색상</Label>
              <ColorGrid>
                {CLASS_COLORS.map((c) => (
                  <ColorBtn key={c} type="button" $color={c} $active={form.color === c} onClick={() => canEdit && setForm({ ...form, color: c })} />
                ))}
              </ColorGrid>
            </FormGroup>
            <FormGroup $full>
              <Label>출석 모드</Label>
              <AttModeToggle>
                <AttModeBtn $active={form.attendanceMode === 'self'} onClick={() => canEdit && setForm({ ...form, attendanceMode: 'self' })}>
                  위치 기반 (학생)
                </AttModeBtn>
                <AttModeBtn $active={form.attendanceMode === 'manual'} onClick={() => canEdit && setForm({ ...form, attendanceMode: 'manual' })}>
                  자동 (강사 체크)
                </AttModeBtn>
              </AttModeToggle>
            </FormGroup>
            <FormGroup $full>
              <ColorSaveRow>
                {canEdit && (
                  <PrimaryBtn onClick={saveBasicInfo} disabled={saving || !form.name}>
                    {saving ? '저장 중...' : '저장'}
                  </PrimaryBtn>
                )}
              </ColorSaveRow>
            </FormGroup>
          </FormGrid>
        </SectionModal>
      )}

      {/* ══ 정규 수업 모달 ══ */}
      {activeModal === 'schedule-regular' && (
        <SectionModal title="정규 수업 관리" onClose={closeModal}>
          {schedules.length > 0 ? (
            <ChipList>
              {schedules.map((s, i) => (
                <Chip key={i}>
                  매주 {s.day}요일 {s.time}{s.endTime ? ` ~ ${s.endTime}` : ''}
                  {canEdit && <ChipRemove onClick={() => removeSchedule(i)}><IoCloseCircle size={16} /></ChipRemove>}
                </Chip>
              ))}
            </ChipList>
          ) : <EmptyMsg>등록된 정규 수업이 없습니다</EmptyMsg>}
          {canEdit && (
            <AddBox>
              <AddBoxLabel>정규 수업 추가</AddBoxLabel>
              <InlineAddRow>
                <SmallSelect value={newDay} onChange={(e) => setNewDay(e.target.value)}>
                  {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
                </SmallSelect>
                <SmallInput type="time" value={newTime} onChange={(e) => setNewTime(e.target.value)} />
                <span style={{ fontSize: 13, color: '#9CA3AF' }}>~</span>
                <SmallInput type="time" value={newEndTime} onChange={(e) => setNewEndTime(e.target.value)} />
                <SmallPrimaryBtn onClick={addSchedule} disabled={!newTime}><HiPlus size={14} /> 추가</SmallPrimaryBtn>
              </InlineAddRow>
            </AddBox>
          )}
        </SectionModal>
      )}

      {/* ══ 보강 수업 모달 ══ */}
      {activeModal === 'schedule-supplement' && (
        <SectionModal title="보강 수업 관리" onClose={closeModal}>
          {supplements.length > 0 ? (
            <ChipList>
              {supplements.map((s, i) => (
                <Chip key={i} $supplement>
                  {friendlyDate(s.date)} {s.time}{s.endTime ? ` ~ ${s.endTime}` : ''}
                  {canEdit && <ChipRemove onClick={() => removeSupplement(i)}><IoCloseCircle size={16} /></ChipRemove>}
                </Chip>
              ))}
            </ChipList>
          ) : <EmptyMsg>등록된 보강 수업이 없습니다</EmptyMsg>}
          {canEdit && (
            <AddBox>
              <AddBoxLabel>보강 수업 추가</AddBoxLabel>
              <InlineAddRow>
                <SmallInput type="date" value={supDate} onChange={(e) => setSupDate(e.target.value)} style={{ maxWidth: 150 }} />
                <SmallInput type="time" value={supTime} onChange={(e) => setSupTime(e.target.value)} />
                <span style={{ fontSize: 13, color: '#9CA3AF' }}>~</span>
                <SmallInput type="time" value={supEndTime} onChange={(e) => setSupEndTime(e.target.value)} />
                <SmallPrimaryBtn onClick={addSupplement} disabled={!supDate || !supTime}><HiPlus size={14} /> 추가</SmallPrimaryBtn>
              </InlineAddRow>
            </AddBox>
          )}
        </SectionModal>
      )}

      {/* ══ 수강 학생 모달 ══ */}
      {activeModal === 'students' && (
        <SectionModal title="수강 학생" onClose={closeModal}>
          <StudentSectionLabel>현재 수강 중 <StudentSectionCount>{studentIds.length}명</StudentSectionCount></StudentSectionLabel>
          {studentIds.length > 0 ? (
            <StudentListBox>
              {studentIds.map((sid) => {
                const st = allStudents.find(s => s.uid === sid);
                return (
                  <StudentRow key={sid}>
                    <StudentRowLeft>
                      <StudentAvatar>{(st?.name || '?')[0]}</StudentAvatar>
                      <StudentRowInfo>
                        <ListRowName>{st?.name || sid}</ListRowName>
                        <ListRowMeta>{st?.school || ''}{st?.grade ? ` · ${st.grade}` : ''}</ListRowMeta>
                      </StudentRowInfo>
                    </StudentRowLeft>
                    {canEdit && <StudentRemoveBtn onClick={() => removeStudent(sid)}>제거</StudentRemoveBtn>}
                  </StudentRow>
                );
              })}
            </StudentListBox>
          ) : <EmptyMsg>등록된 수강 학생이 없습니다</EmptyMsg>}

          {canEdit && (
            <>
              <StudentDivider />
              <StudentSectionLabel>학생 추가 <StudentSectionCount>{filteredStudents.length}명 추가 가능</StudentSectionCount></StudentSectionLabel>
              <SearchRow>
                <IoSearchOutline size={18} color="#9CA3AF" />
                <SearchInput placeholder="학생 이름 검색..." value={studentSearch} onChange={(e) => setStudentSearch(e.target.value)} />
              </SearchRow>
              <ListBox>
                {filteredStudents.map((s) => (
                  <ListRow key={s.uid} onClick={() => addStudent(s.uid)}>
                    <StudentAvatar $add>{(s.name || '?')[0]}</StudentAvatar>
                    <ListRowName>{s.name}</ListRowName>
                    {s.school && <ListRowMeta>{s.school}</ListRowMeta>}
                    {s.grade && <ListRowBadge>{s.grade}</ListRowBadge>}
                    <ListRowAction><HiPlus size={14} /></ListRowAction>
                  </ListRow>
                ))}
                {filteredStudents.length === 0 && <EmptyRow>{studentSearch ? '검색 결과가 없습니다' : '추가할 학생이 없습니다'}</EmptyRow>}
              </ListBox>
            </>
          )}
        </SectionModal>
      )}

      {/* ══ 수업 자료 모달 ══ */}
      {activeModal === 'materials' && (
        <SectionModal title="수업 자료" onClose={closeModal}>
          {materials.length > 0 && (
            <MatCardGrid>
              {materials.map((m) => (
                <MatCard key={m.id}>
                  <MatCardTop>
                    <MatTypeBadge>{m.fileType || '파일'}</MatTypeBadge>
                    <MatCardActions>
                      {m.fileUrl && <LinkBtn href={m.fileUrl} target="_blank" rel="noopener noreferrer">다운로드</LinkBtn>}
                      {canEdit && <DangerSmallBtn onClick={() => removeMaterial(m)}><HiOutlineTrash size={14} /></DangerSmallBtn>}
                    </MatCardActions>
                  </MatCardTop>
                  <MatCardTitle>{m.title}</MatCardTitle>
                  {m.description && <MatCardDesc>{m.description}</MatCardDesc>}
                  <MatCardMeta>{m.fileName && `${m.fileName} · `}{formatDate(m.createdAt)}</MatCardMeta>
                </MatCard>
              ))}
            </MatCardGrid>
          )}
          {canEdit && (
            <MatAddArea>
              <MatAddRow>
                <SmallInput placeholder="" value={matForm.title} onChange={(e) => { const v = e.target.value; setMatForm(prev => ({ ...prev, title: v })); }} style={{ flex: 1 }} />
                <MatFilePickBtn type="button" onClick={() => setShowMaterialPicker(true)}>
                  {matForm.libraryItem ? matForm.libraryItem.fileName || matForm.libraryItem.title : '파일 선택 (자료실)'}
                </MatFilePickBtn>
                <SmallPrimaryBtn onClick={addMaterial} disabled={!matForm.title || !matForm.libraryItem}><HiPlus size={14} /> 추가</SmallPrimaryBtn>
              </MatAddRow>
              <SmallInput placeholder="" value={matForm.description} onChange={(e) => { const v = e.target.value; setMatForm(prev => ({ ...prev, description: v })); }} style={{ width: '100%', marginTop: 8 }} />
            </MatAddArea>
          )}
          {materials.length === 0 && !canEdit && <EmptyMsg>등록된 수업 자료가 없습니다</EmptyMsg>}
        </SectionModal>
      )}

      {/* 자료실 파일 선택 피커 */}
      {showMaterialPicker && (
        <PickerOverlay onClick={() => setShowMaterialPicker(false)}>
          <PickerModal onClick={(e) => e.stopPropagation()}>
            <PickerHeader>
              <PickerTitle>자료실에서 파일 선택</PickerTitle>
              <PickerCloseBtn onClick={() => setShowMaterialPicker(false)}><IoCloseCircle size={22} /></PickerCloseBtn>
            </PickerHeader>
            <PickerSearch>
              <IoSearchOutline size={16} color="#9CA3AF" />
              <PickerSearchInput placeholder="자료 검색 (제목, 태그)..." value={materialSearch} onChange={(e) => setMaterialSearch(e.target.value)} />
            </PickerSearch>
            <PickerList>
              {filteredLibraryMaterials.map((lm) => (
                <PickerItem key={lm.id} onClick={() => selectLibraryFile(lm)}>
                  <PickerItemInfo>
                    <PickerItemTitle>{lm.title}</PickerItemTitle>
                    <PickerItemMeta>
                      {lm.fileType || '파일'}{lm.category && ` · ${lm.category}`}{lm.fileName && ` · ${lm.fileName}`}
                    </PickerItemMeta>
                  </PickerItemInfo>
                  <PickerSelectText>선택</PickerSelectText>
                </PickerItem>
              ))}
              {filteredLibraryMaterials.length === 0 && (
                <PickerEmpty>{materialSearch ? '검색 결과가 없습니다' : '자료실에 등록된 자료가 없습니다'}</PickerEmpty>
              )}
            </PickerList>
          </PickerModal>
        </PickerOverlay>
      )}

      {/* ══ 수업 동영상 모달 ══ */}
      {activeModal === 'videos' && (
        <SectionModal title="수업 동영상" onClose={closeModal}>
          {videos.length > 0 && (
            <MatCardGrid>
              {videos.map((v) => (
                <MatCard key={v.id}>
                  <MatCardTop>
                    <MatTypeBadge>{v.fileType || '동영상'}</MatTypeBadge>
                    <MatCardActions>
                      {v.fileUrl && <LinkBtn href={v.fileUrl} target="_blank" rel="noopener noreferrer">보기</LinkBtn>}
                      {canEdit && <DangerSmallBtn onClick={() => removeVideo(v)}><HiOutlineTrash size={14} /></DangerSmallBtn>}
                    </MatCardActions>
                  </MatCardTop>
                  <MatCardTitle>{v.title}</MatCardTitle>
                  {v.description && <MatCardDesc>{v.description}</MatCardDesc>}
                  <MatCardMeta>{v.fileName && `${v.fileName} · `}{formatDate(v.createdAt)}</MatCardMeta>
                </MatCard>
              ))}
            </MatCardGrid>
          )}
          {canEdit && (
            <MatAddArea>
              <MatAddRow>
                <SmallInput placeholder="" value={vidForm.title} onChange={(e) => setVidForm({ ...vidForm, title: e.target.value })} style={{ flex: 1 }} />
                <MatFilePickBtn type="button" onClick={() => setShowVideoPicker(true)}>
                  {vidForm.libraryItem ? vidForm.libraryItem.fileName || vidForm.libraryItem.title : '파일 선택 (자료실)'}
                </MatFilePickBtn>
                <SmallPrimaryBtn onClick={addVideo} disabled={!vidForm.title || !vidForm.libraryItem}><HiPlus size={14} /> 추가</SmallPrimaryBtn>
              </MatAddRow>
              <SmallInput placeholder="" value={vidForm.description} onChange={(e) => setVidForm({ ...vidForm, description: e.target.value })} style={{ width: '100%', marginTop: 8 }} />
            </MatAddArea>
          )}
          {videos.length === 0 && !canEdit && <EmptyMsg>등록된 수업 동영상이 없습니다</EmptyMsg>}
        </SectionModal>
      )}

      {/* 동영상 자료실 피커 */}
      {showVideoPicker && (
        <PickerOverlay onClick={() => setShowVideoPicker(false)}>
          <PickerModal onClick={(e) => e.stopPropagation()}>
            <PickerHeader>
              <PickerTitle>자료실에서 파일 선택</PickerTitle>
              <PickerCloseBtn onClick={() => setShowVideoPicker(false)}><IoCloseCircle size={22} /></PickerCloseBtn>
            </PickerHeader>
            <PickerSearch>
              <IoSearchOutline size={16} color="#9CA3AF" />
              <PickerSearchInput placeholder="자료 검색 (제목, 태그)..." value={videoSearch} onChange={(e) => setVideoSearch(e.target.value)} />
            </PickerSearch>
            <PickerList>
              {filteredLibraryVideos.map((lm) => (
                <PickerItem key={lm.id} onClick={() => selectLibraryVideo(lm)}>
                  <PickerItemInfo>
                    <PickerItemTitle>{lm.title}</PickerItemTitle>
                    <PickerItemMeta>
                      {lm.fileType || '파일'}{lm.category && ` · ${lm.category}`}{lm.fileName && ` · ${lm.fileName}`}
                    </PickerItemMeta>
                  </PickerItemInfo>
                  <PickerSelectText>선택</PickerSelectText>
                </PickerItem>
              ))}
              {filteredLibraryVideos.length === 0 && (
                <PickerEmpty>{videoSearch ? '검색 결과가 없습니다' : '자료실에 등록된 자료가 없습니다'}</PickerEmpty>
              )}
            </PickerList>
          </PickerModal>
        </PickerOverlay>
      )}

      {/* ══ 출석 관리 모달 ══ */}
      {activeModal === 'attendance' && (
        <SectionModal title={`출석 관리 (${isSelfMode ? '위치 기반' : '자동'})`} wide onClose={closeModal}>
          {studentIds.length === 0 ? (
            <EmptyMsg>수강 학생을 먼저 등록해주세요</EmptyMsg>
          ) : (
            <>
              <CalendarWrap>
                <CalMonthHeader>
                  <CalNavBtn onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() - 1, 1))}>
                    <HiChevronLeft size={18} />
                  </CalNavBtn>
                  <CalMonthTitle>{calMonth.getFullYear()}년 {calMonth.getMonth() + 1}월</CalMonthTitle>
                  <CalNavBtn onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 1))}>
                    <HiChevronRight size={18} />
                  </CalNavBtn>
                </CalMonthHeader>
                <CalWeekRow>
                  {['일', '월', '화', '수', '목', '금', '토'].map((d, di) => (
                    <CalWeekCell key={d} $sun={di === 0} $sat={di === 6}>{d}</CalWeekCell>
                  ))}
                </CalWeekRow>
                <CalGrid>
                  {getCalendarDays().map((dayNum, i) => {
                    const colIdx = i % 7;
                    if (!dayNum) return <CalDayCell key={i} />;
                    const { regular, supplement, dateStr, beforeOpen } = getSchedulesForDay(dayNum);
                    const today = toLocalDateStr(new Date());
                    const isToday = dateStr === today;
                    if (beforeOpen) return (
                      <CalDayCell key={i} $disabled>
                        <CalDayNum $disabled $sun={colIdx === 0} $sat={colIdx === 6}>{dayNum}</CalDayNum>
                      </CalDayCell>
                    );
                    return (
                      <CalDayCell key={i}>
                        <CalDayRow>
                          <CalDayLeft>
                            <CalDayNum $sun={colIdx === 0} $sat={colIdx === 6}>{dayNum}</CalDayNum>
                            {regular && (() => {
                              const sum = getAttendanceSummary(dateStr, 'regular');
                              return sum && (
                                <CalAttSummary>
                                  {sum.present > 0 && <CalSumItem $type="present">출석{sum.present}</CalSumItem>}
                                  {sum.late > 0 && <CalSumItem $type="late">지각{sum.late}</CalSumItem>}
                                  {sum.absent > 0 && <CalSumItem $type="absent">결석{sum.absent}</CalSumItem>}
                                  {sum.excused > 0 && <CalSumItem $type="excused">사유{sum.excused}</CalSumItem>}
                                </CalAttSummary>
                              );
                            })()}
                            {supplement && (() => {
                              const sum = getAttendanceSummary(dateStr, 'supplement');
                              return sum && (
                                <CalAttSummary>
                                  {sum.present > 0 && <CalSumItem $type="present">출석{sum.present}</CalSumItem>}
                                  {sum.late > 0 && <CalSumItem $type="late">지각{sum.late}</CalSumItem>}
                                  {sum.absent > 0 && <CalSumItem $type="absent">결석{sum.absent}</CalSumItem>}
                                  {sum.excused > 0 && <CalSumItem $type="excused">사유{sum.excused}</CalSumItem>}
                                </CalAttSummary>
                              );
                            })()}
                          </CalDayLeft>
                          <CalDayTags>
                            {regular && regular.map((s, si) => {
                              const selfRec = attendanceRecords.find((r) => r.date === dateStr && r.type === 'regular');
                              const selfOpen = selfRec?.selfCheckOpen;
                              return (
                                <React.Fragment key={`r-${si}`}>
                                  <CalTag
                                    $type="regular"
                                    $selected={attendanceDate === dateStr && attendanceType === 'regular'}
                                    $done={hasAttendanceRecord(dateStr, 'regular')}
                                    onClick={() => selectScheduleForAttendance(dateStr, 'regular')}
                                  >정규 {s.time}{s.endTime ? `~${s.endTime}` : ''}</CalTag>
                                  {isSelfMode && canEdit && dateStr <= toLocalDateStr(new Date()) && (
                                    <SelfToggleBtn $open={selfOpen} onClick={(e) => { e.stopPropagation(); toggleSelfCheck(dateStr, 'regular'); }}>
                                      {selfOpen ? '마감' : '열기'}
                                    </SelfToggleBtn>
                                  )}
                                </React.Fragment>
                              );
                            })}
                            {supplement && (() => {
                              const selfRec = attendanceRecords.find((r) => r.date === dateStr && r.type === 'supplement');
                              const selfOpen = selfRec?.selfCheckOpen;
                              return (
                                <React.Fragment>
                                  <CalTag
                                    $type="supplement"
                                    $selected={attendanceDate === dateStr && attendanceType === 'supplement'}
                                    $done={hasAttendanceRecord(dateStr, 'supplement')}
                                    onClick={() => selectScheduleForAttendance(dateStr, 'supplement')}
                                  >보강 {supplement.time}{supplement.endTime ? `~${supplement.endTime}` : ''}</CalTag>
                                  {isSelfMode && canEdit && dateStr <= toLocalDateStr(new Date()) && (
                                    <SelfToggleBtn $open={selfOpen} onClick={(e) => { e.stopPropagation(); toggleSelfCheck(dateStr, 'supplement'); }}>
                                      {selfOpen ? '마감' : '열기'}
                                    </SelfToggleBtn>
                                  )}
                                </React.Fragment>
                              );
                            })()}
                          </CalDayTags>
                        </CalDayRow>
                      </CalDayCell>
                    );
                  })}
                </CalGrid>
              </CalendarWrap>

            </>
          )}
        </SectionModal>
      )}

      {/* 출석 체크 팝업 */}
      {attendanceDate && canEdit && (() => {
        const isFuture = attendanceDate > toLocalDateStr(new Date());
        return (
          <SubPopupOverlay onClick={() => { setAttendanceDate(null); setAttendanceType(''); }}>
            <SubPopupContent onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{friendlyDate(attendanceDate)} {attendanceType === 'regular' ? '정규수업' : '보강수업'} 출석</ModalTitle>
                <ModalCloseBtn onClick={() => { setAttendanceDate(null); setAttendanceType(''); }}><IoCloseCircle size={24} /></ModalCloseBtn>
              </ModalHeader>
              <ModalBody>
                {isFuture ? (
                  <AttFutureMsg>아직 수업 전입니다. 출석 체크는 수업 당일부터 가능합니다.</AttFutureMsg>
                ) : (
                  <>
                    <AttCardGrid>
                      {studentIds.map((sid) => {
                        const current = attendanceMap[sid] || { status: 'present', note: '' };
                        return (
                          <AttCard key={sid}>
                            <AttCardNameRow>
                              <AttCardName>{getStudentName(sid)}</AttCardName>
                              <AttNoteToggle
                                $hasNote={!!current.note}
                                onClick={() => setAttNoteOpenSid(attNoteOpenSid === sid ? null : sid)}
                              >메모</AttNoteToggle>
                            </AttCardNameRow>
                            <AttStatusRow>
                              {Object.entries(ATTENDANCE_STATUS).map(([key, label]) => (
                                <AttStatusBtn
                                  key={key}
                                  $active={current.status === key}
                                  $statusKey={key}
                                  onClick={() => setAttendanceMap({ ...attendanceMap, [sid]: { ...current, status: key } })}
                                >{label}</AttStatusBtn>
                              ))}
                            </AttStatusRow>
                            {attNoteOpenSid === sid && (
                              <AttNoteInput
                                placeholder=""
                                value={current.note}
                                autoFocus
                                onChange={(e) => setAttendanceMap({ ...attendanceMap, [sid]: { ...current, note: e.target.value } })}
                              />
                            )}
                          </AttCard>
                        );
                      })}
                    </AttCardGrid>
                    <SaveRow>
                      <PrimaryBtn onClick={saveAttendance}>출석 저장</PrimaryBtn>
                    </SaveRow>
                  </>
                )}
              </ModalBody>
            </SubPopupContent>
          </SubPopupOverlay>
        );
      })()}

      {/* ══ 과제 관리 모달 ══ */}
      {activeModal === 'assignments' && (
        <SectionModal title="과제 관리" wide onClose={closeModal}>
          <CalendarWrap>
            <CalMonthHeader>
              <CalNavBtn onClick={() => setAssignCalMonth(new Date(assignCalMonth.getFullYear(), assignCalMonth.getMonth() - 1, 1))}><HiChevronLeft size={18} /></CalNavBtn>
              <CalMonthTitle>{assignCalMonth.getFullYear()}년 {assignCalMonth.getMonth() + 1}월</CalMonthTitle>
              <CalNavBtn onClick={() => setAssignCalMonth(new Date(assignCalMonth.getFullYear(), assignCalMonth.getMonth() + 1, 1))}><HiChevronRight size={18} /></CalNavBtn>
            </CalMonthHeader>
            <CalWeekRow>
              {['일', '월', '화', '수', '목', '금', '토'].map((d, di) => (
                <CalWeekCell key={d} $sun={di === 0} $sat={di === 6}>{d}</CalWeekCell>
              ))}
            </CalWeekRow>
            <CalGrid>
              {getCalDays(assignCalMonth).map((dayNum, i) => {
                if (!dayNum) return <CalDayCell key={i} />;
                const colIdx = i % 7;
                const dateStr = toDateStr(assignCalMonth, dayNum);
                const today = toLocalDateStr(new Date());
                const isToday = dateStr === today;
                const dayAssigns = getAssignmentsForDate(dateStr);
                return (
                  <CalDayCell key={i} onClick={() => { setAssignSelDate(dateStr); setAssignForm({ ...assignForm, dueDate: dateStr }); }} style={{ cursor: 'pointer' }}>
                    <CalDayRow>
                      <CalDayLeft>
                        <CalDayNum $today={isToday} $sun={colIdx === 0} $sat={colIdx === 6}>{dayNum}</CalDayNum>
                        {(() => {
                          const sum = getSubmissionSummary(dateStr);
                          return sum && (
                            <CalAttSummary>
                              {sum.submitted > 0 && <CalSumItem $type="present">제출{sum.submitted}</CalSumItem>}
                              {sum.notSubmitted > 0 && <CalSumItem $type="absent">미제출{sum.notSubmitted}</CalSumItem>}
                            </CalAttSummary>
                          );
                        })()}
                      </CalDayLeft>
                      <CalDayTags>
                        {dayAssigns.map((a) => (
                          <CalEventTag key={a.id} $color="#A855F7">{a.title}</CalEventTag>
                        ))}
                      </CalDayTags>
                    </CalDayRow>
                  </CalDayCell>
                );
              })}
            </CalGrid>
          </CalendarWrap>

        </SectionModal>
      )}

      {/* 과제 상세 팝업 */}
      {assignSelDate && (
        <SubPopupOverlay onClick={() => setAssignSelDate(null)}>
          <SubPopupContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>마감일: {friendlyDate(assignSelDate)}</ModalTitle>
              <ModalCloseBtn onClick={() => setAssignSelDate(null)}><IoCloseCircle size={24} /></ModalCloseBtn>
            </ModalHeader>
            <ModalBody>
              {getAssignmentsForDate(assignSelDate).length > 0 ? (
                <ItemList>
                  {getAssignmentsForDate(assignSelDate).map((a) => (
                    <AssignExpandCard key={a.id}>
                      <AssignExpandHeader>
                        <ItemInfo>
                          <ItemTitle>{a.title}</ItemTitle>
                          <ItemMeta>마감: {friendlyDate(a.dueDate)}{a.description && ` · ${a.description}`}</ItemMeta>
                        </ItemInfo>
                        <ItemActions>
                          {canEdit && <DangerSmallBtn onClick={() => removeAssignment(a)}><HiOutlineTrash size={14} /></DangerSmallBtn>}
                        </ItemActions>
                      </AssignExpandHeader>
                      {studentIds.length > 0 && (
                        <AssignStudentGrid>
                          {studentIds.map((sid) => {
                            const sub = a.submissions?.[sid];
                            const isSubmitted = sub?.status === 'submitted';
                            return (
                              <AssignStudentCard key={sid} $submitted={isSubmitted}>
                                <AssignStudentName>{getStudentName(sid)}</AssignStudentName>
                                <AssignStatusBadge $submitted={isSubmitted}>
                                  {isSubmitted ? '제출' : '미제출'}
                                </AssignStatusBadge>
                                {isSubmitted && sub?.fileUrl && (
                                  <AssignFileLink href={sub.fileUrl} target="_blank" rel="noopener noreferrer">
                                    {sub.fileName || '제출 자료 다운로드'}
                                  </AssignFileLink>
                                )}
                              </AssignStudentCard>
                            );
                          })}
                        </AssignStudentGrid>
                      )}
                    </AssignExpandCard>
                  ))}
                </ItemList>
              ) : (
                <EmptyMsg style={{ marginBottom: 12 }}>이 날짜에 등록된 과제가 없습니다</EmptyMsg>
              )}
              {canEdit && (
                <AddForm>
                  <SmallInput placeholder="과제명" value={assignForm.title} onChange={(e) => setAssignForm({ ...assignForm, title: e.target.value })} style={{ flex: 1 }} />
                  <SmallInput placeholder="설명 (선택)" value={assignForm.description} onChange={(e) => setAssignForm({ ...assignForm, description: e.target.value })} style={{ flex: 1 }} />
                  <SmallPrimaryBtn onClick={addAssignment} disabled={!assignForm.title}><HiPlus size={14} /> 추가</SmallPrimaryBtn>
                </AddForm>
              )}
            </ModalBody>
          </SubPopupContent>
        </SubPopupOverlay>
      )}

      {/* ══ 성적/시험 관리 모달 ══ */}
      {activeModal === 'grades' && (
        <SectionModal title="성적/시험 관리" wide onClose={closeModal}>
          <CalendarWrap>
            <CalMonthHeader>
              <CalNavBtn onClick={() => setGradeCalMonth(new Date(gradeCalMonth.getFullYear(), gradeCalMonth.getMonth() - 1, 1))}><HiChevronLeft size={18} /></CalNavBtn>
              <CalMonthTitle>{gradeCalMonth.getFullYear()}년 {gradeCalMonth.getMonth() + 1}월</CalMonthTitle>
              <CalNavBtn onClick={() => setGradeCalMonth(new Date(gradeCalMonth.getFullYear(), gradeCalMonth.getMonth() + 1, 1))}><HiChevronRight size={18} /></CalNavBtn>
            </CalMonthHeader>
            <CalWeekRow>
              {['일', '월', '화', '수', '목', '금', '토'].map((d, di) => (
                <CalWeekCell key={d} $sun={di === 0} $sat={di === 6}>{d}</CalWeekCell>
              ))}
            </CalWeekRow>
            <CalGrid>
              {getCalDays(gradeCalMonth).map((dayNum, i) => {
                if (!dayNum) return <CalDayCell key={i} />;
                const colIdx = i % 7;
                const dateStr = toDateStr(gradeCalMonth, dayNum);
                const today = toLocalDateStr(new Date());
                const isToday = dateStr === today;
                const dayGrades = getGradesForDate(dateStr);
                return (
                  <CalDayCell key={i} onClick={() => { setGradeSelDate(dateStr); setGradeForm({ ...gradeForm, date: dateStr }); }} style={{ cursor: 'pointer' }}>
                    <CalDayRow>
                      <CalDayLeft>
                        <CalDayNum $today={isToday} $sun={colIdx === 0} $sat={colIdx === 6}>{dayNum}</CalDayNum>
                        {dayGrades.length > 0 && <CalSumItem $type="present">{dayGrades.length}건</CalSumItem>}
                      </CalDayLeft>
                      <CalDayTags>
                        {dayGrades.map((g) => (
                          <CalEventTag key={g.id} $color="#EF4444">{g.examName}</CalEventTag>
                        ))}
                      </CalDayTags>
                    </CalDayRow>
                  </CalDayCell>
                );
              })}
            </CalGrid>
          </CalendarWrap>

        </SectionModal>
      )}

      {/* 성적 상세 팝업 */}
      {gradeSelDate && (
        <SubPopupOverlay onClick={() => setGradeSelDate(null)}>
          <SubPopupContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>시험일: {friendlyDate(gradeSelDate)}</ModalTitle>
              <ModalCloseBtn onClick={() => setGradeSelDate(null)}><IoCloseCircle size={24} /></ModalCloseBtn>
            </ModalHeader>
            <ModalBody>
              {getGradesForDate(gradeSelDate).length === 0 && <EmptyMsg style={{ marginBottom: 12 }}>이 날짜에 등록된 시험이 없습니다</EmptyMsg>}
              {getGradesForDate(gradeSelDate).length > 0 && (
                <ItemList>
                  {getGradesForDate(gradeSelDate).map((g) => (
                    <ItemCard key={g.id} style={{ flexDirection: 'column', gap: 8, cursor: canEdit ? 'pointer' : 'default' }} onClick={() => canEdit && openScoreModal(g)}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <ItemInfo>
                          <ItemTitle>{g.examName}</ItemTitle>
                          <ItemMeta>만점 {g.maxScore}점 · {Object.keys(g.scores || {}).length}/{studentIds.length}명 입력</ItemMeta>
                        </ItemInfo>
                        {canEdit && <DangerSmallBtn onClick={(e) => { e.stopPropagation(); removeGrade(g); }}><HiOutlineTrash size={14} /></DangerSmallBtn>}
                      </div>
                      {g.scores && Object.keys(g.scores).length > 0 && (
                        <ScoreList>
                          {Object.entries(g.scores).map(([sid, score]) => (
                            <ScoreItem key={sid}>{getStudentName(sid)}: <b>{score}</b>점</ScoreItem>
                          ))}
                        </ScoreList>
                      )}
                    </ItemCard>
                  ))}
                </ItemList>
              )}
              {canEdit && (
                <AddForm>
                  <SmallInput placeholder="시험명" value={gradeForm.examName} onChange={(e) => setGradeForm({ ...gradeForm, examName: e.target.value })} style={{ flex: 1 }} />
                  <SmallInput type="number" placeholder="만점" value={gradeForm.maxScore} onChange={(e) => setGradeForm({ ...gradeForm, maxScore: Number(e.target.value) })} style={{ maxWidth: 80 }} />
                  <SmallPrimaryBtn onClick={addGrade}><HiPlus size={14} /> 등록</SmallPrimaryBtn>
                </AddForm>
              )}
            </ModalBody>
          </SubPopupContent>
        </SubPopupOverlay>
      )}

      {/* 성적 입력 모달 */}
      {scoreModal && (
        <PickerOverlay onClick={() => setScoreModal(null)}>
          <PickerModal onClick={(e) => e.stopPropagation()}>
            <PickerHeader>
              <PickerTitle>{scoreModal.examName} — 점수 입력</PickerTitle>
              <PickerCloseBtn onClick={() => setScoreModal(null)}><IoCloseCircle size={22} /></PickerCloseBtn>
            </PickerHeader>
            <div style={{ padding: '16px 20px', fontSize: 13, color: '#6B7280' }}>만점: {scoreModal.maxScore}점</div>
            <PickerList>
              {studentIds.map(sid => {
                const val = scoreInputs[sid] ?? 'absent';
                const isAbsent = val === 'absent';
                return (
                  <ScoreModalRow key={sid}>
                    <ScoreLabel>{getStudentName(sid)}</ScoreLabel>
                    <ScoreModalRight>
                      <SmallInput
                        type="number" placeholder=""
                        value={isAbsent ? '' : val}
                        disabled={isAbsent}
                        onChange={(e) => setScoreInputs({ ...scoreInputs, [sid]: e.target.value })}
                        style={{ width: 100, textAlign: 'center' }}
                      />
                      <ScoreAbsentBtn $active={isAbsent} onClick={() => setScoreInputs({ ...scoreInputs, [sid]: isAbsent ? '' : 'absent' })}>미응시</ScoreAbsentBtn>
                    </ScoreModalRight>
                  </ScoreModalRow>
                );
              })}
            </PickerList>
            <div style={{ padding: '12px 20px', display: 'flex', justifyContent: 'flex-end' }}>
              <SmallPrimaryBtn onClick={saveScores}>저장</SmallPrimaryBtn>
            </div>
          </PickerModal>
        </PickerOverlay>
      )}

      {/* ══ 공지사항 모달 ══ */}
      {activeModal === 'notices' && (
        <SectionModal title="공지사항" onClose={closeModal}>
          {notices.length > 0 && (
            <ItemList>
              {notices.map((n) => (
                <ItemCard key={n.id}>
                  <ItemInfo>
                    <ItemTitle>{n.title}</ItemTitle>
                    {n.content && <div style={{ fontSize: 13, color: '#374151', margin: '4px 0 2px', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{n.content}</div>}
                    <ItemMeta>{formatDate(n.createdAt)}</ItemMeta>
                  </ItemInfo>
                  {canEdit && (
                    <ItemActions>
                      <DangerSmallBtn onClick={() => removeNotice(n)}><HiOutlineTrash size={14} /></DangerSmallBtn>
                    </ItemActions>
                  )}
                </ItemCard>
              ))}
            </ItemList>
          )}
          {canEdit && (
            <AddForm style={{ flexDirection: 'column', gap: 6, alignItems: 'stretch' }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', textAlign: 'left' }}>제목 *</label>
              <SmallInput placeholder="공지 제목을 입력하세요" value={noticeForm.title} onChange={(e) => setNoticeForm({ ...noticeForm, title: e.target.value })} style={{ width: '100%' }} />
              <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginTop: 4, textAlign: 'left' }}>내용</label>
              <TextArea placeholder="공지 내용을 입력하세요 (선택)" value={noticeForm.content} onChange={(e) => setNoticeForm({ ...noticeForm, content: e.target.value })} rows={3} />
              <SmallPrimaryBtn onClick={addNotice} disabled={!noticeForm.title} style={{ alignSelf: 'flex-end' }}><HiPlus size={14} /> 등록</SmallPrimaryBtn>
            </AddForm>
          )}
          {notices.length === 0 && !canEdit && <EmptyMsg>등록된 공지사항이 없습니다</EmptyMsg>}
        </SectionModal>
      )}

      {/* ══ 수업 일지 모달 ══ */}
      {activeModal === 'logs' && (
        <SectionModal title="수업 일지" wide onClose={closeModal}>
          <CalendarWrap>
            <CalMonthHeader>
              <CalNavBtn onClick={() => setLogCalMonth(new Date(logCalMonth.getFullYear(), logCalMonth.getMonth() - 1, 1))}><HiChevronLeft size={18} /></CalNavBtn>
              <CalMonthTitle>{logCalMonth.getFullYear()}년 {logCalMonth.getMonth() + 1}월</CalMonthTitle>
              <CalNavBtn onClick={() => setLogCalMonth(new Date(logCalMonth.getFullYear(), logCalMonth.getMonth() + 1, 1))}><HiChevronRight size={18} /></CalNavBtn>
            </CalMonthHeader>
            <CalWeekRow>
              {['일', '월', '화', '수', '목', '금', '토'].map((d, di) => (
                <CalWeekCell key={d} $sun={di === 0} $sat={di === 6}>{d}</CalWeekCell>
              ))}
            </CalWeekRow>
            <CalGrid>
              {getCalDays(logCalMonth).map((dayNum, i) => {
                if (!dayNum) return <CalDayCell key={i} />;
                const colIdx = i % 7;
                const dateStr = toDateStr(logCalMonth, dayNum);
                const today = toLocalDateStr(new Date());
                const isToday = dateStr === today;
                const dayLogs = getLogsForDate(dateStr);
                return (
                  <CalDayCell key={i} onClick={() => { setLogSelDate(dateStr); setLogForm(f => ({ ...f, date: dateStr, content: '' })); }} style={{ cursor: 'pointer' }}>
                    <CalDayRow>
                      <CalDayLeft>
                        <CalDayNum $today={isToday} $sun={colIdx === 0} $sat={colIdx === 6}>{dayNum}</CalDayNum>
                      </CalDayLeft>
                      <CalDayTags>
                        {dayLogs.map((l) => (
                          <CalEventTag key={l.id} $color="#4A7CFE">{l.content.length > 10 ? l.content.slice(0, 10) + '…' : l.content}</CalEventTag>
                        ))}
                      </CalDayTags>
                    </CalDayRow>
                  </CalDayCell>
                );
              })}
            </CalGrid>
          </CalendarWrap>
          {logs.length === 0 && !canEdit && <EmptyMsg>등록된 수업 일지가 없습니다</EmptyMsg>}
        </SectionModal>
      )}

      {/* 수업 일지 상세/등록 팝업 */}
      {logSelDate && (
        <SubPopupOverlay onClick={() => setLogSelDate(null)}>
          <SubPopupContent onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <ModalHeader>
              <ModalTitle>{friendlyDate(logSelDate)} 수업 일지</ModalTitle>
              <ModalCloseBtn onClick={() => setLogSelDate(null)}><IoCloseCircle size={24} /></ModalCloseBtn>
            </ModalHeader>
            <ModalBody>
              {(() => {
                const viewLogs = logs.filter(l => l.date === logSelDate);
                return viewLogs.length > 0 ? (
                  <ItemList>
                    {viewLogs.map(l => (
                      <LogViewItem key={l.id}>
                        <LogViewContent>{l.content}</LogViewContent>
                        {canEdit && <DangerSmallBtn onClick={() => removeLog(l)}><HiOutlineTrash size={14} /></DangerSmallBtn>}
                      </LogViewItem>
                    ))}
                  </ItemList>
                ) : (
                  <EmptyMsg style={{ marginBottom: 12 }}>이 날짜에 등록된 일지가 없습니다</EmptyMsg>
                );
              })()}
              {canEdit && (
                <AddForm style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                  <TextArea placeholder="수업 내용을 입력하세요" value={logForm.content} onChange={(e) => setLogForm({ ...logForm, content: e.target.value })} rows={6} />
                  <SmallPrimaryBtn onClick={addLog} disabled={!logForm.content} style={{ alignSelf: 'flex-end' }}><HiPlus size={14} /> 기록 추가</SmallPrimaryBtn>
                </AddForm>
              )}
            </ModalBody>
          </SubPopupContent>
        </SubPopupOverlay>
      )}

      {/* ══ 메모 모달 ══ */}
      {activeModal === 'memos' && (
        <SectionModal title="메모" onClose={closeModal}>
          {memos.length > 0 && (
            <ItemList>
              {memos.map((m) => (
                <MemoCard key={m.id}>
                  <MemoCardHeader>
                    {m.title && <MemoCardTitle>{m.title}</MemoCardTitle>}
                    <MemoCardDate>{formatDate(m.createdAt)}</MemoCardDate>
                    {canEdit && (
                      <DangerSmallBtn onClick={() => removeMemo(m)}><HiOutlineTrash size={14} /></DangerSmallBtn>
                    )}
                  </MemoCardHeader>
                  <MemoCardContent>{m.content}</MemoCardContent>
                </MemoCard>
              ))}
            </ItemList>
          )}
          {canEdit && (
            <AddForm style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <SmallInput placeholder="메모 제목 (선택)" value={memoForm.title} onChange={(e) => setMemoForm({ ...memoForm, title: e.target.value })} style={{ width: '100%' }} />
              <TextArea placeholder="메모 내용을 입력하세요" value={memoForm.content} onChange={(e) => setMemoForm({ ...memoForm, content: e.target.value })} rows={4} />
              <SmallPrimaryBtn onClick={addMemo} disabled={!memoForm.content} style={{ alignSelf: 'flex-end' }}><HiPlus size={14} /> 추가</SmallPrimaryBtn>
            </AddForm>
          )}
          {memos.length === 0 && !canEdit && <EmptyMsg>등록된 메모가 없습니다</EmptyMsg>}
        </SectionModal>
      )}
    </Container>
  );
};

export default AdminClassDetailPage;

// ═══════════════════════════════════════
// Styled Components
// ═══════════════════════════════════════
const Container = styled.div`padding-bottom:40px;`;
const DeleteOverlay = styled.div`position:fixed;inset:0;background:rgba(0,0,0,0.6);display:flex;flex-direction:column;align-items:center;justify-content:center;z-index:9999;`;
const DeleteSpinner = styled.div`width:40px;height:40px;border:4px solid rgba(255,255,255,0.3);border-top-color:white;border-radius:50%;animation:spin 0.8s linear infinite;@keyframes spin{to{transform:rotate(360deg)}}`;
const DeleteText = styled.p`color:white;font-size:16px;font-weight:600;margin-top:16px;`;
const LoadingText = styled.p`text-align:center;padding:40px;color:#9CA3AF;font-size:14px;`;

// Top bar
const TopBar = styled.div`margin-bottom:24px;`;
const BackBtn = styled.button`display:flex;align-items:center;gap:6px;font-size:13px;color:#6B7280;margin-bottom:12px;&:hover{color:#111827;}`;
const TopRight = styled.div`display:flex;justify-content:space-between;align-items:center;`;
const ClassTitle = styled.h1`display:flex;align-items:center;gap:10px;font-size:22px;font-weight:700;color:#111827;`;
const ColorDot = styled.div`width:12px;height:12px;border-radius:50%;background:${({ $color }) => $color};`;
const DeleteClassBtn = styled.button`display:flex;align-items:center;gap:4px;padding:6px 14px;border-radius:8px;font-size:13px;font-weight:600;color:#EF4444;background:#FEF2F2;&:hover{background:#FEE2E2;}`;

// Top 3 grid
const TopThreeGrid = styled.div`display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin-bottom:12px;align-items:stretch;@media(max-width:1200px){grid-template-columns:1fr;}`;

// Section (collapsible)
const Section = styled.div`background:white;border-radius:12px;border:1px solid #EAEDF3;margin-bottom:12px;overflow:hidden;display:flex;flex-direction:column;${TopThreeGrid} &{margin-bottom:0;}`;
const SectionHeader = styled.button`
  width:100%;display:flex;justify-content:space-between;align-items:center;
  padding:16px 20px;cursor:pointer;
  &:hover{background:#FAFBFC;}
`;
const SectionHeaderLeft = styled.div`display:flex;align-items:center;gap:8px;`;
const SectionTitle = styled.h2`font-size:15px;font-weight:700;color:#111827;margin:0;`;
const Badge = styled.span`font-size:12px;font-weight:600;color:#4A7CFE;background:#EEF2FF;padding:2px 8px;border-radius:10px;`;
const SectionBody = styled.div`padding:0 20px 20px;flex:1;`;

// Form
const FormGrid = styled.div`display:grid;grid-template-columns:1fr 1fr;gap:16px;`;
const FormGroup = styled.div`${({ $full }) => $full && 'grid-column:1/-1;'}`;
const Label = styled.label`display:block;font-size:13px;font-weight:600;color:#374151;margin-bottom:6px;`;
const Input = styled.input`width:100%;padding:10px 12px;border:1px solid #E5E7EB;border-radius:8px;font-size:14px;color:#111827;box-sizing:border-box;&:focus{border-color:#4A7CFE;outline:none;}&:disabled{background:#F9FAFB;color:#6B7280;}`;
const Select = styled.select`width:100%;padding:10px 12px;border:1px solid #E5E7EB;border-radius:8px;font-size:14px;color:#111827;&:disabled{background:#F9FAFB;}`;
const FixedValue = styled.div`padding:10px 12px;border:1px solid #E5E7EB;border-radius:8px;font-size:14px;color:#6B7280;background:#F9FAFB;`;
const TextArea = styled.textarea`width:100%;padding:10px 12px;border:1px solid #E5E7EB;border-radius:8px;font-size:14px;color:#111827;resize:vertical;box-sizing:border-box;font-family:inherit;&:focus{border-color:#4A7CFE;outline:none;}`;

// Log Calendar
const MiniCalArrow = styled.button`background:none;border:none;cursor:pointer;color:#6B7280;font-size:16px;padding:4px;&:hover{color:#111827;}`;
const MiniCalTitle = styled.span`font-size:14px;font-weight:700;color:#111827;`;
const MiniCalWD = styled.div`text-align:center;font-size:12px;font-weight:700;padding:6px 0;color:${({$sun,$sat})=>$sun?'#EF4444':$sat?'#4A7CFE':'#9CA3AF'};`;

const LogCalWrap = styled.div`background:#FAFBFC;border:1px solid #EAEDF3;border-radius:12px;padding:16px;margin-bottom:16px;`;
const LogCalNav = styled.div`display:flex;align-items:center;justify-content:center;gap:16px;margin-bottom:12px;`;
const LogCalWeek = styled.div`display:grid;grid-template-columns:repeat(7,1fr);border-bottom:1px solid #E5E7EB;`;
const LogCalGrid = styled.div`display:grid;grid-template-columns:repeat(7,1fr);`;
const LogCalCell = styled.div`
  min-height:72px;padding:6px;border-bottom:1px solid #F3F4F6;border-right:1px solid #F3F4F6;cursor:pointer;
  background:${({$active})=>$active?'#EEF2FF':'transparent'};
  &:nth-child(7n){border-right:none;}
  &:hover{background:${({$active})=>$active?'#EEF2FF':'#F9FAFB'};}
`;
const LogCalNum = styled.div`
  font-size:13px;font-weight:600;margin-bottom:4px;
  color:${({$sun,$sat,$today,$active})=>$active?'#4A7CFE':$today?'white':$sun?'#EF4444':$sat?'#4A7CFE':'#374151'};
  ${({$today})=>$today&&'display:inline-flex;align-items:center;justify-content:center;width:24px;height:24px;border-radius:50%;background:#4A7CFE;'}
`;
const LogCalTag = styled.div`font-size:11px;font-weight:600;color:#4A7CFE;background:#EEF2FF;border-radius:4px;padding:2px 5px;white-space:nowrap;`;

const LogViewBox = styled.div`background:white;border:1px solid #E5E7EB;border-radius:10px;padding:16px;margin-bottom:16px;`;
const LogViewHeader = styled.div`display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;`;
const LogViewDateTitle = styled.div`font-size:14px;font-weight:700;color:#111827;`;
const LogViewItem = styled.div`display:flex;align-items:flex-start;justify-content:space-between;gap:8px;padding:10px 0;border-top:1px solid #F3F4F6;&:first-child{border-top:none;padding-top:0;}`;
const LogViewContent = styled.div`font-size:14px;color:#374151;line-height:1.6;white-space:pre-wrap;flex:1;`;
const LogFormDateLabel = styled.div`font-size:13px;font-weight:600;color:#4A7CFE;margin-bottom:4px;`;

const ColorSaveRow = styled.div`display:flex;align-items:center;justify-content:space-between;gap:12px;`;
const ColorGrid = styled.div`display:flex;gap:8px;`;
const ColorBtn = styled.button`
  width:28px;height:28px;border-radius:50%;background:${({ $color }) => $color};cursor:pointer;
  border:3px solid ${({ $active, $color }) => $active ? $color : 'transparent'};
  outline:${({ $active }) => $active ? '2px solid #E5E7EB' : 'none'};outline-offset:1px;
`;

// Buttons
const SaveRow = styled.div`display:flex;justify-content:flex-end;margin-top:14px;`;
const PrimaryBtn = styled.button`padding:10px 24px;border-radius:8px;background:#4A7CFE;color:white;font-size:14px;font-weight:600;&:hover{background:#3B6DE8;}&:disabled{opacity:0.5;}`;
const SmallPrimaryBtn = styled.button`display:flex;align-items:center;gap:4px;padding:8px 14px;border-radius:8px;background:#4A7CFE;color:white;font-size:13px;font-weight:600;white-space:nowrap;&:hover{background:#3B6DE8;}&:disabled{opacity:0.5;}`;
const DangerSmallBtn = styled.button`display:flex;padding:6px;border-radius:6px;color:#9CA3AF;&:hover{color:#EF4444;background:#FEF2F2;}`;
const LinkBtn = styled.a`font-size:13px;font-weight:600;color:#4A7CFE;padding:6px 10px;border-radius:6px;&:hover{background:#EEF2FF;}text-decoration:none;white-space:nowrap;`;
const SmallBtn = styled.button`padding:8px 14px;border-radius:8px;background:#EEF2FF;color:#4A7CFE;font-size:13px;font-weight:600;&:hover{background:#D9E2FF;}`;
const AttModeToggle = styled.div`display:flex;gap:8px;`;
const AttModeBtn = styled.button`flex:1;padding:10px;border-radius:8px;font-size:14px;font-weight:600;border:2px solid ${({ $active }) => $active ? '#4A7CFE' : '#E5E7EB'};background:${({ $active }) => $active ? '#EEF2FF' : 'white'};color:${({ $active }) => $active ? '#4A7CFE' : '#6B7280'};`;
const SelfToggleBtn = styled.button`padding:2px 8px;border-radius:4px;font-size:10px;font-weight:700;background:${({ $open }) => $open ? '#EF4444' : '#22C55E'};color:white;margin-left:2px;`;

// Chips
const ChipList = styled.div`display:flex;flex-wrap:wrap;gap:8px;margin-bottom:12px;`;
const Chip = styled.div`
  display:flex;align-items:center;gap:6px;padding:6px 14px;border-radius:16px;font-size:13px;font-weight:500;
  ${({ $accent, $supplement }) => $accent
    ? 'background:#4A7CFE;color:white;font-weight:700;'
    : $supplement
    ? 'background:#FEE2E2;color:#DC2626;border:1px solid #FECACA;'
    : 'background:#F3F4F6;color:#374151;'}
`;
const ChipRemove = styled.button`display:flex;color:inherit;opacity:0.6;&:hover{opacity:1;color:#EF4444;}`;

// Schedule sub-labels
const ScheduleSubLabel = styled.p`font-size:14px;font-weight:700;color:#374151;margin:0 0 10px;`;
const ScheduleDivider = styled.div`height:1px;background:#E5E7EB;margin:20px 0;`;

// Inline add row
const AddBox = styled.div`margin-top:14px;padding:14px;background:#F9FAFB;border:1px dashed #D1D5DB;border-radius:10px;`;
const AddBoxLabel = styled.p`font-size:12px;font-weight:600;color:#9CA3AF;margin:0 0 8px;`;
const InlineAddRow = styled.div`display:flex;align-items:center;gap:8px;flex-wrap:wrap;justify-content:center;`;
const SmallSelect = styled.select`padding:8px 12px;border:1px solid #E5E7EB;border-radius:8px;font-size:14px;`;
const SmallInput = styled.input`padding:8px 12px;border:1px solid #E5E7EB;border-radius:4px;font-size:14px;box-sizing:border-box;&:focus{border-color:#4A7CFE;outline:none;}`;
const AddForm = styled.div`display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:12px;`;

// Search
const SearchRow = styled.div`display:flex;align-items:center;gap:8px;padding:8px 12px;border:1px solid #E5E7EB;border-radius:8px;margin-bottom:8px;`;
const SearchInput = styled.input`flex:1;border:none;font-size:14px;&::placeholder{color:#9CA3AF;}&:focus{outline:none;}`;

// List
const ListBox = styled.div`border:1px solid #E5E7EB;border-radius:8px;max-height:200px;overflow-y:auto;`;
const ListRow = styled.button`width:100%;display:flex;align-items:center;gap:10px;padding:10px 12px;border-bottom:1px solid #F3F4F6;text-align:left;&:last-child{border:none;}&:hover{background:#F0F7FF;}`;
const ListRowName = styled.span`font-size:14px;font-weight:600;color:#111827;`;
const ListRowMeta = styled.span`font-size:12px;color:#9CA3AF;`;
const ListRowBadge = styled.span`font-size:12px;color:#6B7280;background:#F3F4F6;padding:1px 6px;border-radius:4px;`;
const ListRowAction = styled.span`margin-left:auto;color:#4A7CFE;display:flex;`;
const EmptyRow = styled.div`padding:16px;text-align:center;color:#9CA3AF;font-size:13px;`;
const EmptyMsg = styled.p`font-size:13px;color:#9CA3AF;margin:0 0 12px;`;

// Item list (materials, videos, etc.)
const ItemList = styled.div`display:flex;flex-direction:column;gap:8px;margin-bottom:12px;`;
const ItemCard = styled.div`display:flex;align-items:center;justify-content:space-between;padding:12px 16px;background:#FAFBFC;border-radius:8px;border:1px solid #F3F4F6;`;
const ItemInfo = styled.div`flex:1;min-width:0;`;
const ItemTitle = styled.p`font-size:14px;font-weight:600;color:#111827;margin:0;`;
const ItemMeta = styled.p`font-size:12px;color:#9CA3AF;margin:2px 0 0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;`;
const ItemActions = styled.div`display:flex;align-items:center;gap:4px;margin-left:12px;flex-shrink:0;`;

// Calendar
const CalendarWrap = styled.div`
  background:#FFFFFF;border:1px solid #EAEDF3;border-radius:12px;padding:20px;margin-bottom:16px;
  box-shadow:0 1px 3px rgba(0,0,0,0.04);
`;
const CalMonthHeader = styled.div`display:flex;align-items:center;justify-content:center;gap:20px;margin-bottom:16px;`;
const CalMonthTitle = styled.span`font-size:18px;font-weight:800;color:#111827;min-width:140px;text-align:center;letter-spacing:-0.3px;`;
const CalNavBtn = styled.button`
  display:flex;align-items:center;justify-content:center;width:32px;height:32px;
  border-radius:8px;color:#6B7280;transition:all 0.15s;
  &:hover{background:#EEF2FF;color:#4A7CFE;}
`;
const CalWeekRow = styled.div`display:grid;grid-template-columns:repeat(7,1fr);margin-bottom:6px;border-bottom:1px solid #F3F4F6;padding-bottom:8px;`;
const CalWeekCell = styled.div`
  text-align:center;font-size:16px;font-weight:700;padding:8px 0;
  color:#6B7280;
`;
const CalGrid = styled.div`display:grid;grid-template-columns:repeat(7,1fr);`;
const CalDayCell = styled.div`
  min-height:80px;padding:8px;border:1px solid #F3F4F6;
  ${({ $disabled }) => $disabled && 'background:#FAFAFA;opacity:0.4;'}
`;
const CalDayRow = styled.div`display:flex;flex-direction:column;gap:4px;height:100%;`;
const CalDayLeft = styled.div`display:flex;flex-direction:column;gap:2px;`;
const CalDayNum = styled.span`
  font-size:18px;font-weight:700;
  color:${({ $disabled }) => $disabled ? '#D1D5DB' : '#374151'};
`;
const CalDayTags = styled.div`display:flex;flex-wrap:wrap;gap:3px;align-items:flex-start;`;
const CalEventTag = styled.span`
  font-size:11px;font-weight:700;padding:3px 10px;border-radius:6px;
  color:white;background:${({ $color }) => $color};
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100px;
`;
const CalAttSummary = styled.div`display:flex;gap:3px;flex-wrap:wrap;`;
const CalSumItem = styled.span`
  font-size:13px;font-weight:700;
  color:${({ $type }) => {
    if ($type === 'present') return '#4A7CFE';
    if ($type === 'late') return '#F59E0B';
    if ($type === 'absent') return '#EF4444';
    return '#9CA3AF';
  }};
`;
const CalTag = styled.button`
  font-size:13px;font-weight:700;padding:4px 12px;border-radius:4px;cursor:pointer;
  width:fit-content;transition:all 0.15s;letter-spacing:-0.2px;
  ${({ $type, $selected, $done }) => {
    if ($selected) return 'background:#4A7CFE;color:white;box-shadow:0 2px 4px rgba(74,124,254,0.3);';
    if ($done && $type === 'regular') return 'background:#D1FAE5;color:#059669;';
    if ($done && $type === 'supplement') return 'background:#FEE2E2;color:#DC2626;';
    if ($type === 'regular') return 'background:#EEF2FF;color:#4A7CFE;&:hover{background:#DBEAFE;transform:scale(1.05);}';
    return 'background:#FEE2E2;color:#DC2626;&:hover{background:#FECACA;transform:scale(1.05);}';
  }}
`;

// Attendance panel
const AttendancePanel = styled.div`
  background:#FFFFFF;border:1.5px solid ${({ $disabled }) => $disabled ? '#E5E7EB' : '#4A7CFE'};
  border-radius:12px;padding:20px;margin-top:8px;
  box-shadow:0 4px 12px ${({ $disabled }) => $disabled ? 'rgba(0,0,0,0.03)' : 'rgba(74,124,254,0.1)'};
  ${({ $disabled }) => $disabled && 'opacity:0.5;'}
`;
const AttPanelHeader = styled.div`display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;`;
const AttPanelTitle = styled.span`font-size:15px;font-weight:700;color:#111827;`;
const AttFutureMsg = styled.p`text-align:center;padding:24px 0;font-size:14px;color:#9CA3AF;font-weight:500;`;

// Attendance cards
const AttCardGrid = styled.div`display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:10px;margin-bottom:16px;`;
const AttCard = styled.div`border-radius:10px;padding:14px;border:1px solid #EAEDF3;background:#FAFBFC;`;
const AttCardNameRow = styled.div`display:flex;align-items:center;gap:6px;margin:0 0 10px;`;
const AttCardName = styled.span`font-size:14px;font-weight:700;color:#111827;`;
const AttNoteToggle = styled.button`
  font-size:11px;padding:2px 8px;border-radius:4px;font-weight:600;
  background:${({ $hasNote }) => $hasNote ? '#4A7CFE18' : '#F3F4F6'};
  color:${({ $hasNote }) => $hasNote ? '#4A7CFE' : '#9CA3AF'};
  border:1px solid ${({ $hasNote }) => $hasNote ? '#4A7CFE40' : 'transparent'};
`;
const AttStatusRow = styled.div`display:flex;gap:4px;flex-wrap:wrap;margin-bottom:8px;`;
const AttStatusBtn = styled.button`
  font-size:12px;font-weight:600;padding:4px 10px;border-radius:6px;transition:all 0.15s;
  ${({ $active, $statusKey }) => {
    if (!$active) return 'background:#F3F4F6;color:#9CA3AF;&:hover{background:#E5E7EB;color:#374151;}';
    const colors = { present: '#22C55E', late: '#F59E0B', absent: '#EF4444', excused: '#6B7280' };
    const c = colors[$statusKey] || '#6B7280';
    return `background:${c};color:white;`;
  }}
`;
const AttNoteInput = styled.input`
  width:100%;padding:6px 10px;border:1px solid #E5E7EB;border-radius:6px;
  font-size:12px;color:#374151;box-sizing:border-box;
  &::placeholder{color:#C4C4C4;}&:focus{border-color:#4A7CFE;outline:none;}
`;

// Grades
const ScoreList = styled.div`display:flex;flex-wrap:wrap;gap:8px;width:100%;`;
const ScoreItem = styled.span`font-size:13px;color:#374151;background:#F3F4F6;padding:4px 10px;border-radius:6px;`;
const ScoreInputGrid = styled.div`margin-top:12px;`;
const ScoreInputRow = styled.div`display:flex;align-items:center;gap:12px;margin-bottom:6px;`;
const ScoreLabel = styled.span`font-size:14px;color:#111827;min-width:80px;`;
const ScoreModalRow = styled.div`display:flex;align-items:center;justify-content:space-between;padding:10px 20px;border-bottom:1px solid #F3F4F6;`;
const ScoreModalRight = styled.div`display:flex;align-items:center;gap:8px;`;
const ScoreAbsentBtn = styled.button`
  padding:7px 12px;border-radius:8px;font-size:13px;cursor:pointer;white-space:nowrap;
  border:1px solid ${({ $active }) => $active ? '#EF4444' : '#D1D5DB'};
  background:${({ $active }) => $active ? '#FEF2F2' : 'white'};
  color:${({ $active }) => $active ? '#EF4444' : '#6B7280'};
  font-weight:${({ $active }) => $active ? 600 : 400};
`;

// Material cards (grid)
const MatCardGrid = styled.div`display:grid;grid-template-columns:repeat(auto-fill,minmax(240px,1fr));gap:12px;margin-bottom:12px;`;
const MatCard = styled.div`background:#FAFBFC;border:1px solid #F3F4F6;border-radius:10px;padding:14px;display:flex;flex-direction:column;gap:6px;`;
const MatCardTop = styled.div`display:flex;justify-content:space-between;align-items:center;`;
const MatTypeBadge = styled.span`font-size:11px;font-weight:700;color:white;background:#374151;padding:3px 10px;border-radius:5px;`;
const MatCardActions = styled.div`display:flex;align-items:center;gap:2px;`;
const MatCardTitle = styled.p`font-size:14px;font-weight:700;color:#111827;margin:0;`;
const MatCardDesc = styled.p`font-size:13px;color:#6B7280;margin:0;`;
const MatCardMeta = styled.p`font-size:11px;color:#9CA3AF;margin:0;`;
const MatAddArea = styled.div`margin-top:12px;`;
const MatAddRow = styled.div`display:flex;align-items:center;gap:8px;`;
const MatFilePickBtn = styled.button`padding:8px 14px;border:1px dashed #D1D5DB;border-radius:8px;font-size:13px;color:#6B7280;background:#FAFBFC;white-space:nowrap;max-width:220px;overflow:hidden;text-overflow:ellipsis;&:hover{border-color:#4A7CFE;color:#4A7CFE;background:#F0F4FF;}`;

// Picker modal (material library)
const PickerOverlay = styled.div`position:fixed;inset:0;background:rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;z-index:50;`;
const PickerModal = styled.div`background:white;border-radius:16px;padding:0;width:560px;max-width:90vw;max-height:80vh;display:flex;flex-direction:column;overflow:hidden;`;
const PickerHeader = styled.div`display:flex;justify-content:space-between;align-items:center;padding:20px 20px 0;`;
const PickerTitle = styled.h2`font-size:18px;font-weight:700;color:#111827;margin:0;`;
const PickerCloseBtn = styled.button`display:flex;color:#9CA3AF;&:hover{color:#6B7280;}`;
const PickerSearch = styled.div`display:flex;align-items:center;gap:8px;margin:16px 20px;padding:8px 12px;border:1px solid #E5E7EB;border-radius:8px;`;
const PickerSearchInput = styled.input`flex:1;border:none;font-size:14px;&::placeholder{color:#9CA3AF;}&:focus{outline:none;}`;
const PickerList = styled.div`flex:1;overflow-y:auto;padding:0 20px 20px;`;
const PickerItem = styled.button`width:100%;display:flex;align-items:center;justify-content:space-between;padding:12px 14px;border-radius:8px;text-align:left;border:1px solid #F3F4F6;margin-bottom:8px;&:hover{background:#F0F7FF;border-color:#DBEAFE;}`;
const PickerItemInfo = styled.div`flex:1;min-width:0;`;
const PickerItemTitle = styled.p`font-size:14px;font-weight:600;color:#111827;margin:0;`;
const PickerItemMeta = styled.p`font-size:12px;color:#9CA3AF;margin:2px 0 0;`;
const PickerSelectText = styled.span`font-size:13px;font-weight:600;color:#4A7CFE;white-space:nowrap;`;
const PickerEmpty = styled.div`padding:24px;text-align:center;color:#9CA3AF;font-size:13px;`;

// Assignment student submission cards
const AssignExpandCard = styled.div`background:#FAFBFC;border:1px solid #F3F4F6;border-radius:10px;padding:16px;`;
const AssignExpandHeader = styled.div`display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;`;
const AssignStudentGrid = styled.div`display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:8px;`;
const AssignStudentCard = styled.div`
  border-radius:8px;padding:12px;border:1px solid #EAEDF3;background:white;
`;
const AssignStudentName = styled.p`font-size:13px;font-weight:700;color:#111827;margin:0 0 6px;`;
const AssignStatusBadge = styled.span`
  display:inline-block;font-size:12px;font-weight:600;padding:3px 10px;border-radius:6px;margin-bottom:4px;
  ${({ $submitted }) => $submitted
    ? 'background:#D1FAE5;color:#059669;'
    : 'background:#FEF2F2;color:#EF4444;'}
`;
const AssignFileLink = styled.a`display:block;font-size:11px;font-weight:600;color:#4A7CFE;text-decoration:none;margin-top:2px;&:hover{text-decoration:underline;}`;

// Card Grid
const CardGrid = styled.div`display:grid;grid-template-columns:repeat(auto-fill,minmax(320px,1fr));gap:20px;margin-bottom:28px;`;
const DashCard = styled.button`
  display:flex;align-items:center;gap:20px;padding:28px 24px;
  background:white;border-radius:16px;border:1px solid #EAEDF3;
  text-align:left;transition:all 0.2s;
  &:hover{border-color:#D1D5DB;box-shadow:0 6px 16px rgba(0,0,0,0.07);transform:translateY(-2px);}
`;
const DashCardIcon = styled.div`
  width:54px;height:54px;border-radius:14px;display:flex;align-items:center;justify-content:center;flex-shrink:0;
  background:${({ $bg }) => $bg || '#F3F4F6'};color:${({ $color }) => $color || '#6B7280'};
  svg{width:26px;height:26px;}
`;
const DashCardInfo = styled.div`flex:1;min-width:0;`;
const DashCardTitle = styled.p`font-size:16px;font-weight:700;color:#111827;margin:0 0 4px;`;
const DashCardSub = styled.p`font-size:13px;font-weight:600;color:#374151;margin:0 0 6px;`;
const DashCardDetail = styled.p`font-size:12px;color:#374151;font-weight:600;margin:0;line-height:1.5;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;`;

// Basic Info Card (custom)
const BasicInfoCard = styled.button`
  grid-column:1/-1;display:flex;flex-direction:column;gap:0;
  background:white;border-radius:16px;border:1px solid #EAEDF3;
  text-align:left;transition:all 0.2s;overflow:hidden;
  &:hover{border-color:#D1D5DB;box-shadow:0 6px 16px rgba(0,0,0,0.07);}
`;
const BasicInfoTop = styled.div`display:flex;gap:0;padding:0;`;
const BasicInfoItem = styled.div`flex:1;padding:20px 24px;border-right:1px solid #F3F4F6;&:last-child{border-right:none;}`;
const BasicInfoLabel = styled.div`font-size:12px;font-weight:600;color:#9CA3AF;margin-bottom:6px;`;
const BasicInfoValue = styled.div`font-size:16px;font-weight:700;color:#111827;`;
const BasicInfoSchedule = styled.div`
  padding:16px 24px;border-top:1px solid #F3F4F6;background:#FAFBFC;cursor:pointer;
  &:hover{background:#F0F4FF;}
`;
const BasicInfoScheduleList = styled.div`display:flex;flex-wrap:wrap;gap:12px;margin-top:6px;`;
const BasicInfoScheduleChip = styled.span`
  font-size:14px;font-weight:700;color:#111827;background:white;
  padding:8px 18px;border-radius:10px;border:1px solid #E5E7EB;
`;
const BasicInfoSupChip = styled.span`
  font-size:14px;font-weight:700;color:#DC2626;background:#FEE2E2;
  padding:8px 18px;border-radius:10px;border:1px solid #FECACA;
`;

const BasicInfoScheduleAddChip = styled.button`
  display:flex;align-items:center;gap:4px;
  font-size:14px;font-weight:700;color:white;background:#4A7CFE;
  padding:8px 18px;border-radius:10px;border:none;
  cursor:pointer;
  &:hover{background:#3B6DE8;}
`;

// Student Section
const StudentSectionLabel = styled.div`font-size:14px;font-weight:700;color:#374151;margin-bottom:12px;display:flex;align-items:center;gap:8px;`;
const StudentSectionCount = styled.span`font-size:13px;font-weight:600;color:#4A7CFE;background:#EEF2FF;padding:2px 10px;border-radius:10px;`;
const StudentListBox = styled.div`display:flex;flex-direction:column;gap:6px;`;
const StudentRow = styled.div`display:flex;align-items:center;justify-content:space-between;padding:10px 14px;background:#F9FAFB;border-radius:10px;border:1px solid #F3F4F6;`;
const StudentRowLeft = styled.div`display:flex;align-items:center;gap:10px;`;
const StudentRowInfo = styled.div`display:flex;flex-direction:column;gap:3px;`;
const StudentAvatar = styled.div`
  width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;
  font-size:13px;font-weight:700;
  ${({ $add }) => $add
    ? 'background:#F3F4F6;color:#9CA3AF;'
    : 'background:#F3F4F6;color:#6B7280;'}
`;
const StudentRemoveBtn = styled.button`font-size:12px;font-weight:600;color:#fff;padding:4px 12px;border-radius:6px;background:#EF4444;&:hover{background:#DC2626;}`;
const StudentDivider = styled.div`height:1px;background:#E5E7EB;margin:20px 0;`;

// Memo Card
const MemoCard = styled.div`background:#FAFBFC;border:1px solid #F3F4F6;border-radius:10px;padding:16px;`;
const MemoCardHeader = styled.div`display:flex;align-items:center;gap:8px;margin-bottom:8px;`;
const MemoCardTitle = styled.span`font-size:14px;font-weight:700;color:#111827;flex:1;`;
const MemoCardDate = styled.span`font-size:12px;color:#9CA3AF;margin-left:auto;`;
const MemoCardContent = styled.p`font-size:14px;color:#374151;line-height:1.6;white-space:pre-wrap;margin:0;`;

// Section Modal
const ModalOverlay = styled.div`position:fixed;inset:0;background:rgba(0,0,0,0.45);display:flex;align-items:center;justify-content:center;z-index:40;padding:20px;`;
const ModalContent = styled.div`
  background:white;border-radius:16px;width:${({ $wide }) => $wide ? '960px' : '600px'};
  max-width:95vw;max-height:90vh;display:flex;flex-direction:column;overflow:hidden;
  box-shadow:0 20px 60px rgba(0,0,0,0.15);
`;
const ModalHeader = styled.div`display:flex;justify-content:space-between;align-items:center;padding:20px 24px;background:#EEF2FF;border-bottom:1px solid #D9E2FF;flex-shrink:0;`;
const ModalTitle = styled.h2`font-size:18px;font-weight:700;color:#1E3A5F;margin:0;`;
const ModalCloseBtn = styled.button`display:flex;color:#9CA3AF;&:hover{color:#6B7280;}`;
const ModalBody = styled.div`padding:24px;overflow-y:auto;flex:1;`;

const SectionModal = ({ title, wide, onClose, children }) => (
  <ModalOverlay onClick={onClose}>
    <ModalContent $wide={wide} onClick={e => e.stopPropagation()}>
      <ModalHeader>
        <ModalTitle>{title}</ModalTitle>
        <ModalCloseBtn onClick={onClose}><IoCloseCircle size={24} /></ModalCloseBtn>
      </ModalHeader>
      <ModalBody>{children}</ModalBody>
    </ModalContent>
  </ModalOverlay>
);

// Sub Popup (calendar date detail)
const SubPopupOverlay = styled.div`position:fixed;inset:0;background:rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;z-index:45;padding:20px;`;
const SubPopupContent = styled.div`
  background:white;border-radius:16px;width:680px;max-width:95vw;max-height:85vh;
  display:flex;flex-direction:column;overflow:hidden;
  box-shadow:0 20px 60px rgba(0,0,0,0.2);
`;

// Misc
const Divider = styled.div`height:1px;background:#F3F4F6;margin:16px 0;`;
const SubTitle = styled.h3`font-size:14px;font-weight:600;color:#374151;margin-bottom:10px;`;
